import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { joinPersonName } from '@/utils/names';
import {
  Search, Filter, X, Loader2, ChevronLeft, ChevronRight,
  Download, CheckCircle2, XCircle, Database, Plus, Settings,
  ExternalLink, RefreshCw, Trash2, ToggleLeft, ToggleRight,
  Sparkles, Phone, Mail, Building2,
  ArrowUpRight, Clock, AlertCircle, UserPlus,
  Eye, Edit3, PhoneCall, PhoneOff,
  Send, LayoutList, Columns, TrendingUp,
  MapPin, Package, AlertTriangle, CheckCircle, Ban, ImageIcon,
} from 'lucide-react';
import { api } from '@/utils/api';
import { isCompletePincode, lookupPincode, normalizePincode } from '@/lib/pincodeGeo';
import { LeadExportButton } from './LeadExportButton';
import { canManageLeadSources } from './leadSourceAccess';
import { buildLeadsListParams } from './leadsListParams';
import { useAuthStore } from '@/stores/auth';
import { formatDate, telHref, waHref, formatPhone, dialPhone, toE164 } from '@/utils/formatters';
import { ProductPickerField } from '@/components/shared/ProductPickerField';
import { DispositionPickerModal } from '@/components/leads/DispositionPickerModal';
import { LostReasonBadge } from '@/components/leads/LostReasonBadge';
import { RepeatCustomerChip } from '@/components/leads/RepeatCustomerChip';
import { sourceDisplayLabel, LEAD_SOURCE_KEYS, LEAD_SOURCE_KEY_LABELS, type LeadSourceKey } from './sourceLabel';
import { LeadOwnerFilter, type LeadOwnerBreakdown } from './LeadOwnerFilter';
// Post-create assignment reuses Miss Deepika's triage picker verbatim (sales
// members + dealers), so an admin can hand a just-created lead to a rep/dealer
// on the same success step — same endpoints, no new backend.
import { TriageMemberPicker } from '@/pages/leads/TriageMemberPicker';
import { assignBodyForSelection, type AssignableMember, type AssignableDealer, type AssignableDealerGroup } from '@/pages/leads/triageHelpers';
import { LEAD_STAGES } from './leadStages';
import { LeadReopenButton } from './LeadReopenButton';
import { MarkWonDialog } from './MarkWonDialog';
import { LeadFileImportPreview } from './LeadFileImportPreview';
import { buildColumns, parseCsvText, parseWorkbook, sampleCsv, type ImportColumn, type Table } from './leadFileImport';

// Bumped on every deploy — shown on the lead detail drawer so we can visually
// confirm which build the user is running.
const BUILD_TAG = 'BUILD-2026-04-09-dial-v3';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Lead {
  id: string;
  sourceName: string;
  externalId?: string;
  name?: string;
  company?: string;
  email?: string;
  phone?: string;
  product?: string;
  quantity?: string;
  location?: string;
  message?: string;
  status: string;
  leadStage: string;
  // Sprint Apr-30: canonical 7-stage pipeline
  canonicalStage?: string;
  lostReason?: string;
  lostCompetitorName?: string;
  exactLostReason?: string | null;
  exactLostCompetitorName?: string | null;
  exactLostFreeText?: string | null;
  consecutiveMissedFollowUps?: number;
  isRepeatCustomer?: boolean;
  followUpTotal?: number;
  // metadata for RepeatCustomerChip tooltip
  metadata?: { prevDealValue?: number; prevDealClosedAt?: string; [key: string]: unknown };
  priority: string;
  // assignedToId = canonical User FK (set by mobile field/manual intake +
  // Lead Manager picker). Legacy `assignedTo` String is only populated by the
  // old web reassign path, so field/manual leads carry ONLY the FK. The web
  // "Assigned" column must read the FK first or it mislabels FK-owned leads as
  // "Unassigned" (~414 SS leads). See QA-FIELDLEAD FIX 1.
  assignedToId?: string;
  assignedTo?: string;
  assignedToName?: string;
  notes?: string;
  followUpDate?: string;
  score?: number;
  contactId?: string;
  dealId?: string;
  isDuplicate: boolean;
  createdAt: string;
  updatedAt?: string;
  // Enhanced fields
  phoneStatus?: 'valid' | 'not_functional' | 'incorrect';
  callAttempts?: number;
  lastCalledAt?: string;
  callOutcome?: string;
  interestLevel?: 'hot' | 'warm' | 'cold';
  teleCrmId?: string;
  requirement?: string;
}

interface LeadSource {
  id: string;
  name: string;
  type: string;
  config?: any;
  isActive: boolean;
  lastSyncAt?: string;
  importCount?: number;
}

interface DashboardStats {
  total: number;
  stages: Record<string, number>;
  priorities: Record<string, number>;
  overdueFollowUps: number;
  unassigned: number;
  updatedToday: number;
}

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  // QAF-R3 (2026-07-07): the Employee's linked User FK. The lead owner is stored
  // as `LeadImport.assignedToId` (a User id), so a reassign must send this — not
  // the Employee.id — or `assignedToId` goes stale and the dropdown shows the
  // old owner. The /employees list already returns this column.
  userId?: string | null;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

// ─── Canonical 7-stage pipeline (Sprint Apr-30 2026) ─────────────────────
// 🔒 The list itself now lives in ./leadStages so the dealer-leads panel can
// read the SAME labels (2026-09-24 review M1). Do not re-inline it here.

// Legacy-stage → canonical mapping (read-time adapter for existing 134 SS leads).
// new / first_contact → cold
// follow_up / meeting_required → followup
// quotation_sent / revised_quotation / site_visit / technical_discussion → quote
// negotiation → neg
// order_received / advance_payment / order_processing → hot
// won → won
// dead / lost → lost
const LEGACY_TO_CANONICAL: Record<string, string> = {
  new: 'cold', first_contact: 'cold', contacted: 'cold', qualified: 'cold',
  follow_up: 'followup', meeting_required: 'followup',
  technical_discussion: 'quote', site_visit: 'quote',
  quotation_sent: 'quote', revised_quotation: 'quote', proposal: 'quote',
  negotiation: 'neg',
  order_received: 'hot', advance_payment: 'hot', order_processing: 'hot', converted: 'hot',
  won: 'won',
  dead: 'lost', lost: 'lost',
};

/** Returns the effective canonical stage for a lead (prefers canonicalStage column, falls back to mapping). */
function resolveCanonical(lead: Lead): string {
  if (lead.canonicalStage && LEAD_STAGES.some((s) => s.value === lead.canonicalStage)) {
    return lead.canonicalStage;
  }
  return LEGACY_TO_CANONICAL[lead.leadStage] ?? 'cold';
}

// Kanban columns — one column per canonical stage
const KANBAN_COLUMNS = LEAD_STAGES.map((s) => ({ id: s.value, label: s.label, colorHex: s.colorHex }));

// T6 — mirror of backend LEAD_DATE_TYPES (leads.where-builder.ts). Server
// converts IST calendar dates to the UTC bounds; the client only picks the
// column + the two YYYY-MM-DD dates.
const DATE_TYPES: { value: 'created' | 'assigned' | 'activity'; label: string }[] = [
  { value: 'created', label: 'Created' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'activity', label: 'Last activity' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'gray' },
  { value: 'medium', label: 'Medium', color: 'blue' },
  { value: 'high', label: 'High', color: 'orange' },
  { value: 'urgent', label: 'Urgent', color: 'red' },
];

const LOST_REASON_FILTER_OPTIONS = [
  { value: 'low_budget', label: 'Budget' },
  { value: 'competitor', label: 'Competitor' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'out_of_budget', label: 'Out of Budget' },
  { value: 'dead', label: 'Dead' },
  { value: 'irrelevant', label: 'Irrelevant' },
  { value: 'other', label: 'Other' },
  { value: 'unrecorded', label: 'Reason not recorded' },
] as const;

const INTEREST_LEVELS = [
  { value: 'hot', label: 'Hot', bg: 'bg-red-500/10', text: 'text-red-400' },
  { value: 'warm', label: 'Warm', bg: 'bg-orange-500/10', text: 'text-orange-400' },
  { value: 'cold', label: 'Cold', bg: 'bg-blue-500/10', text: 'text-blue-400' },
];

const CALL_OUTCOMES = [
  { value: 'answered', label: 'Answered' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'busy', label: 'Busy' },
  { value: 'callback_requested', label: 'Callback Requested' },
  { value: 'not_reachable', label: 'Not Reachable' },
  { value: 'interested', label: 'Interested' },
  { value: 'not_interested', label: 'Not Interested' },
];

const SOURCE_TYPE_OPTIONS = ['api', 'scraper'];

const CRM_ACTIVITY_TYPES = [
  { value: 'call', label: 'Phone Call' },
  { value: 'email', label: 'Email Sent' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'site_visit', label: 'Site Visit' },
  { value: 'technical_discussion', label: 'Technical Discussion' },
  { value: 'quotation_sent', label: 'Quotation Sent' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'whatsapp', label: 'WhatsApp Message' },
  { value: 'sms', label: 'SMS Sent' },
  { value: 'note', label: 'Note Added' },
  { value: 'demo', label: 'Product Demo' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'payment_received', label: 'Payment Received' },
  { value: 'order_confirmed', label: 'Order Confirmed' },
  { value: 'status_change', label: 'Status Change' },
];
const PAGE_SIZE = 20;

const STAGE_BADGE: Record<string, { bg: string; text: string }> = {
  new:                  { bg: 'bg-blue-500/10',    text: 'text-blue-400' },
  first_contact:        { bg: 'bg-cyan-500/10',    text: 'text-cyan-400' },
  follow_up:            { bg: 'bg-sky-500/10',     text: 'text-sky-400' },
  technical_discussion: { bg: 'bg-indigo-500/10',  text: 'text-indigo-400' },
  site_visit:           { bg: 'bg-violet-500/10',  text: 'text-violet-400' },
  quotation_sent:       { bg: 'bg-purple-500/10',  text: 'text-purple-400' },
  meeting_required:     { bg: 'bg-fuchsia-500/10', text: 'text-fuchsia-400' },
  negotiation:          { bg: 'bg-orange-500/10',  text: 'text-orange-400' },
  revised_quotation:    { bg: 'bg-amber-500/10',   text: 'text-amber-400' },
  order_received:       { bg: 'bg-lime-500/10',    text: 'text-lime-400' },
  advance_payment:      { bg: 'bg-green-500/10',   text: 'text-green-400' },
  order_processing:     { bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
  won:                  { bg: 'bg-green-600/15',   text: 'text-green-300' },
  dead:                 { bg: 'bg-gray-500/10',    text: 'text-gray-400' },
  lost:                 { bg: 'bg-red-500/10',     text: 'text-red-400' },
  // Legacy
  contacted:            { bg: 'bg-cyan-500/10',    text: 'text-cyan-400' },
  qualified:            { bg: 'bg-indigo-500/10',  text: 'text-indigo-400' },
  proposal:             { bg: 'bg-purple-500/10',  text: 'text-purple-400' },
  converted:            { bg: 'bg-green-500/10',   text: 'text-green-400' },
};


const SOURCE_BADGE: Record<string, { bg: string; text: string }> = {
  manual:       { bg: 'bg-gray-500/10',    text: 'text-gray-400' },
  indiamart:    { bg: 'bg-orange-500/10',  text: 'text-orange-400' },
  website:      { bg: 'bg-blue-500/10',    text: 'text-blue-400' },
  social_media: { bg: 'bg-pink-500/10',    text: 'text-pink-400' },
  inbound_call: { bg: 'bg-green-500/10',   text: 'text-green-400' },
  walkin:       { bg: 'bg-teal-500/10',    text: 'text-teal-400' },
  reference:    { bg: 'bg-yellow-500/10',  text: 'text-yellow-400' },
  whatsapp:     { bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
  webhook:          { bg: 'bg-purple-500/10',  text: 'text-purple-400' },
  api:              { bg: 'bg-indigo-500/10',  text: 'text-indigo-400' },
  stellar_sanitar:  { bg: 'bg-cyan-500/10',    text: 'text-cyan-400' },
  ai_chatbox:       { bg: 'bg-violet-500/10',  text: 'text-violet-400' },
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function LeadsPage() {
  // Data
  const [leads, setLeads] = useState<Lead[]>([]);
  const [dashStats, setDashStats] = useState<DashboardStats | null>(null);
  const [meta, setMeta] = useState<PaginationMeta>({ total: 0, page: 1, limit: PAGE_SIZE, totalPages: 1 });
  // T12 — per-stage counts from the same filter object as the list rows
  // and header total (spec §3 counts contract), replacing dashStats.stages.
  const [stageCounts, setStageCounts] = useState<Record<string, number>>({});
  // whole filtered book, independent of the active stage tab (see leads.service.ts)
  const [bookTotal, setBookTotal] = useState(0);
  const [sources, setSources] = useState<LeadSource[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterLostReason, setFilterLostReason] = useState('');
  // T6 — multi-select 7-key source chip (replaces the old single raw-sourceName
  // dropdown; the shared endpoint's `source` param is a CSV of these keys).
  const [filterSourceKeys, setFilterSourceKeys] = useState<LeadSourceKey[]>([]);
  const [filterCustomerType, setFilterCustomerType] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [filterOwnerName, setFilterOwnerName] = useState('');
  const [ownerBreakdown, setOwnerBreakdown] = useState<LeadOwnerBreakdown>({
    canFilterByOwner: false,
    owners: [],
    unassignedCount: 0,
  });
  // T6 — date-range chip (dateType + IST calendar from/to).
  const [filterDateType, setFilterDateType] = useState<'created' | 'assigned' | 'activity'>('created');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');

  // Panels
  const [showImportPanel, setShowImportPanel] = useState(false);
  const [showAddLead, setShowAddLead] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [showAddSource, setShowAddSource] = useState(false);
  const [sourceForm, setSourceForm] = useState({ name: '', type: 'api' as string, config: '{}' });
  const [savingSource, setSavingSource] = useState(false);

  // Lead form product categories
  const [leadFormCategories, setLeadFormCategories] = useState<{ id: string; name: string }[]>([]);

  // Add Lead form
  const [leadForm, setLeadForm] = useState({
    sourceName: 'manual',
    name: '', company: '', email: '', phone: '', product: '', quantity: '',
    location: '', locationState: '', pincode: '', message: '', priority: 'medium',
    interestLevel: 'warm', requirement: '',
  });
  // Pincode → city/state autofill (reuses GET /geo/pincode/:code, the same
  // endpoint the mobile app uses). label = what the pincode resolved to.
  const [pinLookup, setPinLookup] = useState<{ loading: boolean; found: boolean | null; label: string }>({ loading: false, found: null, label: '' });
  const pinReqRef = useRef(0);
  // 🔒 2026-09-24 — the lookup itself moved to `lib/pincodeGeo` so the dealer
  // forms could reuse it instead of growing a second copy. The behaviour here
  // is unchanged: every edit invalidates the request before it (including
  // shortening or clearing), otherwise a slow six-digit answer refills City
  // after the user erased it.
  const handlePincode = useCallback((raw: string) => {
    const pin = normalizePincode(raw);
    const reqId = ++pinReqRef.current;
    setLeadForm((f) => ({ ...f, pincode: pin }));
    if (!isCompletePincode(pin)) { setPinLookup({ loading: false, found: null, label: '' }); return; }
    setPinLookup({ loading: true, found: null, label: '' });
    void lookupPincode(pin).then((hit) => {
      if (reqId !== pinReqRef.current) return; // a newer lookup superseded this
      if (hit.found) {
        setLeadForm((f) => ({ ...f, location: hit.city || f.location, locationState: hit.state }));
      }
      setPinLookup({ loading: false, found: hit.found, label: hit.label });
    });
  }, []);

  // Post-create assign picker — opens after a lead is created so the creator can
  // hand it to a sales member or a dealer (same picker + endpoints as triage).
  const [assignFor, setAssignFor] = useState<{ id: string; name: string } | null>(null);
  const [assignMembers, setAssignMembers] = useState<AssignableMember[] | null>(null);
  const [assignMembersLoading, setAssignMembersLoading] = useState(false);
  const [assignDealerGroups, setAssignDealerGroups] = useState<AssignableDealerGroup[] | null>(null);
  const [assignDealersLoading, setAssignDealersLoading] = useState(false);
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignFlash, setAssignFlash] = useState('');
  // Reference/collaborator tagging on new leads
  const [refs, setRefs] = useState<{ type: string; name: string; lastName: string; phone: string; email: string; customerId: string }[]>([]);
  const [refPhoneLookup, setRefPhoneLookup] = useState<Record<number, { found: boolean; name?: string } | null>>({});
  const [existingContacts, setExistingContacts] = useState<{ id: string; firstName: string; lastName: string; phone?: string | null }[]>([]);
  const [savingLead, setSavingLead] = useState(false);

  // WEB-EDIT (2026-07-07): admin-only primary-contact (name/phone) edit gate.
  // The backend PATCH /lead-imports/:id/details unlocks name/phone ONLY for a
  // server-verified admin/owner/super_admin (canEditPrimaryContact). We mirror
  // that gate on the raw role name so a rep never even sees the affordance.
  // Read the role name directly (NOT the derived dashboard key, which collapses
  // many roles into OWNER); this stays 1:1 with PRIMARY_CONTACT_EDIT_ROLES.
  const authUser = useAuthStore((s) => s.user);
  const canEditContact = useMemo(() => {
    const roleName = (authUser?.role?.name || '').toLowerCase().trim();
    return roleName === 'admin' || roleName === 'owner' || roleName === 'super_admin';
  }, [authUser]);
  // 2026-10-07 (Manak): reps saw Add / switch-off / delete on Leads → Sources.
  // Only admin / owner / super_admin manage sources (backend lead-sources.routes.ts
  // LEAD_SOURCE_ADMIN_ROLES answers 403 to everyone else); others see the list only.
  const canManageSources = canManageLeadSources(authUser?.role?.name);

  // Detail drawer
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [markWonLead, setMarkWonLead] = useState<{ id: string; name: string } | null>(null);
  const [detailNotes, setDetailNotes] = useState('');
  const [detailSaving, setDetailSaving] = useState(false);

  // WEB-EDIT: inline edit-contact (name + phone) form state — admin-only.
  const [showEditContact, setShowEditContact] = useState(false);
  const [editContactForm, setEditContactForm] = useState({ name: '', phone: '' });
  const [savingEditContact, setSavingEditContact] = useState(false);
  const [editContactError, setEditContactError] = useState<string | null>(null);

  // Call log
  const [showCallLog, setShowCallLog] = useState(false);
  const [callForm, setCallForm] = useState({ outcome: 'answered', notes: '' });
  const [loggingCall, setLoggingCall] = useState(false);

  // Post-call prompt (opens after "Call Now")
  const [showPostCallPrompt, setShowPostCallPrompt] = useState(false);
  const [postCallForm, setPostCallForm] = useState({ outcome: 'answered', stage: '', notes: '' });
  const [savingPostCall, setSavingPostCall] = useState(false);

  // Phone status update
  const [updatingPhone, setUpdatingPhone] = useState(false);

  // AURA Sales App push
  const [pushingTeleCrm, setPushingTeleCrm] = useState(false);
  const [teleCrmConfirmedIds, setTeleCrmConfirmedIds] = useState<Set<string>>(new Set());

  // AI scoring
  const [scoringAll, setScoringAll] = useState(false);
  const [scoreProgress, setScoreProgress] = useState({ done: 0, total: 0 });
  const [scoringOne, setScoringOne] = useState<string | null>(null);

  // Pipeline data from lead-capture API (for enriched Kanban view)
  const [pipelineData, setPipelineData] = useState<any>(null);

  // Disposition picker modal (Sprint Apr-30)
  const [dispositionLead, setDispositionLead] = useState<Lead | null>(null);

  // Tenant maxMissedFollowUps (fetched once, default 5)
  const [maxMissedFollowUps, setMaxMissedFollowUps] = useState<number>(5);

  // Capture details form
  const [showCaptureDetails, setShowCaptureDetails] = useState(false);
  const [captureForm, setCaptureForm] = useState({ interest: '', productRequirement: '', location: '', requirement: '', notes: '' });
  const [savingCapture, setSavingCapture] = useState(false);

  // CSV import
  const [importingCsv, setImportingCsv] = useState(false);
  const [fileImport, setFileImport] = useState<{ fileName: string; table: Table; columns: ImportColumn[] } | null>(null);

  // Activity log
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [activityForm, setActivityForm] = useState({ type: 'call', title: '', description: '' });
  const [loggingActivity, setLoggingActivity] = useState(false);
  const [activities, setActivities] = useState<any[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [leadImages, setLeadImages] = useState<{ id: string; filename: string; mimeType: string; size: number; url: string; createdAt: string }[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);
  const [interactions, setInteractions] = useState<any[]>([]);
  const [loadingInteractions, setLoadingInteractions] = useState(false);

  // Actions in progress
  const [processing, setProcessing] = useState<Set<string>>(new Set());
  const latestLeadsRequestId = useRef(0);
  // Create-then-list hand-off. A just-created lead exists only at the top of
  // page 1 of the "All" tab (the endpoint orders createdAt desc and this page
  // never sends sortBy), so the create resets the view to there. If the user's
  // own filters STILL hide it, the next list response says so instead of the
  // screen silently looking unchanged.
  // `clearedNote` is what the create WIPED on the user's behalf. A reset the
  // rep can see is a reset she can undo; a silent one just loses her search.
  const pendingCreatedRef = useRef<{ id: string; name: string; clearedNote: string | null } | null>(null);
  // The last lead created in this session, kept after `pendingCreatedRef` is
  // spent so "Clear filters" can re-ask the server about it instead of
  // declaring the problem solved before the answer arrives.
  const lastCreatedRef = useRef<{ id: string; name: string } | null>(null);
  const [createdNotice, setCreatedNotice] = useState<string | null>(null);
  // The view the notice was raised for. When the rep moves off it, the notice
  // has stopped describing anything and must go — it used to survive a tab
  // change and every later list load for the rest of the session.
  const noticeViewRef = useRef<string | null>(null);

  /* ---- Data fetching ---- */

  // Apr 28 evening — read `scope` URL param. The Lead Manager sidebar
  // sends `?scope=assigned` for "Already-Assigned Oversight" and
  // `?scope=unassigned` for "Verified Pool" (when navigating from the
  // legacy CRM Leads page surface). The backend filter accepts these
  // values — if absent, behavior is unchanged.
  const [searchParams] = useSearchParams();
  const scope = searchParams.get('scope') ?? '';

  const leadFilters = useMemo(() => buildLeadsListParams({
    search, activeTab, filterLostReason, filterPriority, filterSourceKeys,
    filterCustomerType, filterOwner, scope, filterDateFrom, filterDateTo, filterDateType,
  }), [search, activeTab, filterLostReason, filterPriority, filterSourceKeys,
    filterCustomerType, filterOwner, scope, filterDateFrom, filterDateTo, filterDateType]);

  /**
   * 🔒 The identity of the view on screen. The created-lead notice is raised
   * FOR one view; the moment the rep changes tab, search, page or any filter,
   * the sentence is about a screen she is no longer looking at.
   */
  const viewSignature = useMemo(
    () => JSON.stringify([leadFilters, page]),
    [leadFilters, page],
  );
  const viewSignatureRef = useRef(viewSignature);
  viewSignatureRef.current = viewSignature;

  useEffect(() => {
    if (!createdNotice) return;
    if (noticeViewRef.current === null) return;
    if (noticeViewRef.current === viewSignature) return;
    noticeViewRef.current = null;
    setCreatedNotice(null);
  }, [viewSignature, createdNotice]);

  // T6 — swapped onto the shared leads-list endpoint (T2/T3): list rows,
  // header total, and per-stage counts all come from the ONE filter object
  // the backend builds, so they can never disagree (spec §3).
  const fetchLeads = useCallback(async () => {
    const requestId = ++latestLeadsRequestId.current;
    try {
      setLoading(true);
      setError(null);
      const params = { ...leadFilters, skip: (page - 1) * PAGE_SIZE, take: PAGE_SIZE };

      const res = await api.get('/leads-list', { params });
      if (requestId !== latestLeadsRequestId.current) return;
      const rows = res.data.rows ?? [];
      const total = res.data.total ?? 0;
      const nextOwnerBreakdown: LeadOwnerBreakdown = res.data.ownerBreakdown ?? {
        canFilterByOwner: false,
        owners: [],
        unassignedCount: 0,
      };

      setLeads(rows);
      const pendingCreated = pendingCreatedRef.current;
      if (pendingCreated) {
        pendingCreatedRef.current = null;
        const visible = rows.some((row: Lead) => row.id === pendingCreated.id);
        const notice = visible
          ? pendingCreated.clearedNote
          : `${pendingCreated.name} was added, but your current filters hide it.`;
        noticeViewRef.current = notice ? viewSignatureRef.current : null;
        setCreatedNotice(notice);
      }
      setSelected(new Set());
      setMeta({ total, page, limit: PAGE_SIZE, totalPages: Math.max(1, Math.ceil(total / PAGE_SIZE)) });
      // bookTotal ignores the active stage tab (whole filtered book) — used for the
      // Total KPI / In-Pipeline / "All" badge so they don't collapse on tab change.
      setBookTotal(res.data.bookTotal ?? total);
      setStageCounts(res.data.stageCounts ?? {});
      setOwnerBreakdown(nextOwnerBreakdown);
      if (filterOwner && filterOwner !== 'unassigned') {
        const selectedOwner = nextOwnerBreakdown.owners.find((owner) => owner.userId === filterOwner);
        if (selectedOwner) setFilterOwnerName(selectedOwner.name);
      }
      if (!nextOwnerBreakdown.canFilterByOwner && filterOwner) {
        setFilterOwner('');
        setFilterOwnerName('');
        setPage(1);
      }
    } catch (err: any) {
      if (requestId === latestLeadsRequestId.current) {
        pendingCreatedRef.current = null;
        setLeads([]);
        setSelected(new Set());
        setMeta({ total: 0, page, limit: PAGE_SIZE, totalPages: 1 });
        setBookTotal(0);
        setStageCounts({});
        setOwnerBreakdown((current) => ({
          canFilterByOwner: current.canFilterByOwner,
          owners: [],
          unassignedCount: 0,
        }));
        setError(err.response?.data?.error || 'Failed to load leads');
      }
    } finally {
      if (requestId === latestLeadsRequestId.current) setLoading(false);
    }
  }, [leadFilters, page, search, activeTab, filterLostReason, filterPriority, filterSourceKeys, filterCustomerType, filterOwner, scope, filterDateType, filterDateFrom, filterDateTo]);

  const fetchDashStats = useCallback(async () => {
    try {
      const res = await api.get('/lead-integrations/lead-imports/dashboard-stats');
      setDashStats(res.data.data);
    } catch { /* silent */ }
  }, []);

  const fetchSources = useCallback(async () => {
    try {
      setSourcesLoading(true);
      const res = await api.get('/lead-integrations/lead-sources');
      setSources(res.data.data?.sources ?? res.data.data ?? res.data.sources ?? []);
    } catch { /* silent */ } finally { setSourcesLoading(false); }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      // Try to fetch only Sales & Marketing department employees
      let salesDeptId: string | null = null;
      try {
        const deptRes = await api.get('/departments');
        const depts = deptRes.data.data ?? deptRes.data ?? [];
        const salesDept = Array.isArray(depts) ? depts.find((d: any) => d.code === 'SALES') : null;
        if (salesDept) salesDeptId = salesDept.id;
      } catch { /* silent — departments API may not exist */ }

      const params: any = { limit: 100, scope: 'assignable' };
      if (salesDeptId) params.departmentId = salesDeptId;

      const res = await api.get('/employees', { params });
      const data = res.data.data?.employees ?? res.data.data ?? [];
      setEmployees(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, []);

  const fetchPipelineData = useCallback(async () => {
    try {
      const res = await api.get('/lead-capture/pipeline');
      setPipelineData(res.data?.data ?? null);
    } catch { /* silent — pipeline API may not have data yet */ }
  }, []);

  const handleCaptureDetails = async () => {
    if (!selectedLead?.contactId || !captureForm.interest || !captureForm.productRequirement || !captureForm.requirement) return;
    setSavingCapture(true);
    try {
      await api.post('/lead-capture/capture-details', {
        contactId: selectedLead.contactId,
        interest: captureForm.interest,
        productRequirement: captureForm.productRequirement,
        location: captureForm.location || undefined,
        requirement: captureForm.requirement,
        notes: captureForm.notes || undefined,
      });
      const marker = `[Captured] ${captureForm.interest} — ${captureForm.productRequirement}`;
      await api.patch(`/lead-integrations/lead-imports/${selectedLead.id}/details`, {
        notes: `${selectedLead.notes ? `${selectedLead.notes}\n` : ''}${marker}`,
      });
      setSelectedLead((l) => l ? { ...l, notes: `${l.notes ? `${l.notes}\n` : ''}${marker}`, product: captureForm.productRequirement } : null);
      setShowCaptureDetails(false);
      setCaptureForm({ interest: '', productRequirement: '', location: '', requirement: '', notes: '' });
      fetchLeads();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to capture details');
    } finally { setSavingCapture(false); }
  };

  // WEB-EDIT (2026-07-07): open the admin-only edit-contact form, seeded from
  // the selected lead. Guarded by canEditContact — a non-admin can never reach
  // it (the button is not rendered), and the backend re-verifies the role.
  const openEditContact = () => {
    if (!selectedLead || !canEditContact) return;
    setEditContactForm({ name: selectedLead.name || '', phone: selectedLead.phone || '' });
    setEditContactError(null);
    setShowEditContact(true);
  };

  // Save the corrected name/phone via the same details endpoint the backend
  // gates on role (admin/owner/super_admin). source:'admin-edit' is advisory —
  // the server ROLE is the real gate + it audit-logs the phone change.
  const handleEditContact = async () => {
    if (!selectedLead || !canEditContact) return;
    const newName = editContactForm.name.trim();
    const newPhone = editContactForm.phone.trim();
    const digits = newPhone.replace(/\D/g, '');
    if (digits.length < 10) {
      setEditContactError('Phone must have at least 10 digits.');
      return;
    }
    setSavingEditContact(true);
    setEditContactError(null);
    try {
      await api.patch(`/lead-integrations/lead-imports/${selectedLead.id}/details`, {
        name: newName || undefined,
        phone: newPhone,
        source: 'admin-edit',
      });
      setSelectedLead((l) => (l ? { ...l, name: newName || l.name, phone: newPhone } : null));
      setLeads((prev) => prev.map((l) => (l.id === selectedLead.id ? { ...l, name: newName || l.name, phone: newPhone } : l)));
      setShowEditContact(false);
      fetchLeads();
      alert('Contact updated.');
    } catch (err: any) {
      setEditContactError(err.response?.data?.error || 'Failed to update contact.');
    } finally { setSavingEditContact(false); }
  };

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => {
    api.get('/product-settings/categories/lead-form').then((r) => setLeadFormCategories(r.data?.data || [])).catch(() => {});
  }, []);
  useEffect(() => { fetchDashStats(); }, [fetchDashStats]);
  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);
  useEffect(() => { if (showSources || showImportPanel) fetchSources(); }, [showSources, showImportPanel, fetchSources]);
  useEffect(() => { if (viewMode === 'kanban') fetchPipelineData(); }, [viewMode, fetchPipelineData]);
  // Fetch tenant maxMissedFollowUps for "Follow-up #N of M" badge
  useEffect(() => {
    api.get('/admin/tenant-config').then((r) => {
      const cfg = r.data?.data ?? r.data;
      if (cfg?.maxMissedFollowUps) setMaxMissedFollowUps(Number(cfg.maxMissedFollowUps));
    }).catch(() => { /* non-critical — default 5 used */ });
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => { setPage(1); }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => { setPage(1); }, [activeTab]);

  /* ---- Actions ---- */

  const handleProcess = async (id: string) => {
    setProcessing((prev) => new Set(prev).add(id));
    try {
      await api.patch(`/lead-integrations/lead-imports/${id}/process`);
      fetchLeads(); fetchDashStats();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to process lead');
    } finally {
      setProcessing((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const handleReject = async (id: string) => {
    setProcessing((prev) => new Set(prev).add(id));
    try {
      await api.patch(`/lead-integrations/lead-imports/${id}/reject`);
      fetchLeads(); fetchDashStats();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to reject lead');
    } finally {
      setProcessing((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const handleStageChange = async (id: string, stage: string) => {
    try {
      await api.patch(`/lead-integrations/lead-imports/${id}/stage`, { leadStage: stage });

      // Also sync stage on the deal via lead-capture if dealId exists
      const lead = leads.find((l) => l.id === id) || selectedLead;
      if (lead?.dealId) {
        api.patch(`/lead-capture/${lead.dealId}/stage`, { stage }).catch(() => { /* best-effort */ });
      }

      fetchLeads(); fetchDashStats();
      if (selectedLead?.id === id) setSelectedLead((l) => l ? { ...l, leadStage: stage } : null);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to change stage');
    }
  };

  const handleAssign = async (id: string, employeeId: string, employeeName: string) => {
    try {
      // QAF-R3: resolve the picked Employee → its User FK. The canonical owner
      // field is LeadImport.assignedToId (a User id); sending only the
      // Employee.id left assignedToId stale so the dropdown showed the old owner
      // after reassign. Send `assignToUserId` so the backend writes the FK. The
      // backend also resolves Employee→User defensively when only assignedTo is
      // sent (older clients), so this is belt-and-suspenders.
      const emp = employees.find((em) => em.id === employeeId);
      const userFk = emp?.userId || null;
      await api.patch(`/lead-integrations/lead-imports/${id}/assign`, {
        assignedTo: employeeId || null,
        ...(userFk ? { assignToUserId: userFk } : {}),
        assignedToName: employeeName || null,
      });

      // Also create a Deal via lead-capture/assign if the lead has a contactId
      // and no dealId yet. That endpoint's `assignedToUserId` is a User FK.
      const lead = leads.find((l) => l.id === id) || selectedLead;
      if (lead?.contactId && userFk && !lead.dealId) {
        api.post('/lead-capture/assign', {
          contactId: lead.contactId,
          assignedToUserId: userFk,
        }).then((res) => {
          const dealId = res.data?.data?.dealId;
          if (dealId) {
            // Update local state with the new dealId
            setLeads((prev) => prev.map((l) => l.id === id ? { ...l, dealId } : l));
            if (selectedLead?.id === id) setSelectedLead((l) => l ? { ...l, dealId } : null);
          }
        }).catch(() => { /* best-effort deal creation */ });
      }

      fetchLeads(); fetchDashStats();
      // QAF-R3: mirror the canonical User FK into local state so the owner shown
      // is fresh immediately (and consistent with what fetchLeads returns).
      if (selectedLead?.id === id) setSelectedLead((l) => l ? { ...l, assignedToId: userFk || l.assignedToId, assignedTo: employeeId, assignedToName: employeeName } : null);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to assign lead');
    }
  };

  const handlePriorityChange = async (id: string, priority: string) => {
    try {
      await api.patch(`/lead-integrations/lead-imports/${id}/details`, { priority });
      fetchLeads();
      if (selectedLead?.id === id) setSelectedLead((l) => l ? { ...l, priority } : null);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to change priority');
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedLead) return;
    setDetailSaving(true);
    try {
      await api.patch(`/lead-integrations/lead-imports/${selectedLead.id}/details`, { notes: detailNotes });
      setSelectedLead((l) => l ? { ...l, notes: detailNotes } : null);
      fetchLeads();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save notes');
    } finally { setDetailSaving(false); }
  };

  const handleSetFollowUp = async (id: string, date: string) => {
    try {
      await api.patch(`/lead-integrations/lead-imports/${id}/details`, { followUpDate: date || null });
      fetchLeads();
      if (selectedLead?.id === id) setSelectedLead((l) => l ? { ...l, followUpDate: date || undefined } : null);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to set follow-up');
    }
  };

  const handleBulkProcess = async () => {
    if (selected.size === 0) return;
    setBulkProcessing(true);
    try {
      await api.post('/lead-integrations/lead-imports/bulk-process', { ids: Array.from(selected) });
      setSelected(new Set()); fetchLeads(); fetchDashStats();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Bulk process failed');
    } finally { setBulkProcessing(false); }
  };

  /* ---- Phone status ---- */
  const handlePhoneStatus = async (id: string, status: 'valid' | 'not_functional' | 'incorrect') => {
    setUpdatingPhone(true);
    try {
      await api.patch(`/lead-integrations/lead-imports/${id}/details`, { phoneStatus: status });
      setSelectedLead((l) => l ? { ...l, phoneStatus: status } : null);
      setLeads((prev) => prev.map((l) => l.id === id ? { ...l, phoneStatus: status } : l));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update phone status');
    } finally { setUpdatingPhone(false); }
  };

  /* ---- Call log ---- */
  const handleLogCall = async () => {
    if (!selectedLead) return;
    setLoggingCall(true);
    try {
      const callAttempts = (selectedLead.callAttempts || 0) + 1;
      const safeNotes = detailNotes || ''; // guard: null/undefined coerces to "null" when concatenated
      const outcomeLabel = callForm.outcome || 'not_set';
      const marker = `[Call ${callAttempts}] ${outcomeLabel}${callForm.notes ? ': ' + callForm.notes : ''}`;
      const newNotes = safeNotes + (safeNotes ? '\n' : '') + marker;
      await api.patch(`/lead-integrations/lead-imports/${selectedLead.id}/details`, {
        callAttempts,
        callOutcome: callForm.outcome,
        lastCalledAt: new Date().toISOString(),
        notes: newNotes,
      });
      setSelectedLead((l) => l ? { ...l, callAttempts, callOutcome: callForm.outcome, lastCalledAt: new Date().toISOString(), notes: newNotes } : null);
      setDetailNotes(newNotes);
      setCallForm({ outcome: 'answered', notes: '' });
      setShowCallLog(false);
      fetchLeads();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to log call');
    } finally { setLoggingCall(false); }
  };

  /* ---- Post-call prompt save (outcome + stage + notes in one shot) ---- */
  const handleSavePostCall = async () => {
    if (!selectedLead) return;
    setSavingPostCall(true);
    try {
      // 1) Update stage if changed
      if (postCallForm.stage && postCallForm.stage !== selectedLead.leadStage) {
        await handleStageChange(selectedLead.id, postCallForm.stage);
      }
      // 2) Log the call (call attempts, outcome, notes)
      const callAttempts = (selectedLead.callAttempts || 0) + 1;
      const safeNotes = selectedLead.notes || '';
      const outcomeLabel = postCallForm.outcome || 'not_set';
      const marker = `[Call ${callAttempts}] ${outcomeLabel}${postCallForm.notes ? ': ' + postCallForm.notes : ''}`;
      const newNotes = safeNotes + (safeNotes ? '\n' : '') + marker;
      await api.patch(`/lead-integrations/lead-imports/${selectedLead.id}/details`, {
        callAttempts,
        callOutcome: postCallForm.outcome,
        lastCalledAt: new Date().toISOString(),
        notes: newNotes,
      });
      setSelectedLead((l) => l ? {
        ...l,
        callAttempts,
        callOutcome: postCallForm.outcome,
        lastCalledAt: new Date().toISOString(),
        notes: newNotes,
        leadStage: postCallForm.stage || l.leadStage,
      } : null);
      setDetailNotes(newNotes);
      setPostCallForm({ outcome: 'answered', stage: '', notes: '' });
      setShowPostCallPrompt(false);
      fetchLeads();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save call outcome');
    } finally { setSavingPostCall(false); }
  };

  /* ---- AURA Sales App push ---- */
  const handlePushTeleCrm = async (id: string) => {
    if (!confirm('Push this lead to AURA Sales App?')) return;
    setPushingTeleCrm(true);
    try {
      await api.post(`/lead-integrations/lead-imports/${id}/telecrm`);

      // Also push to lead-capture telecaller if lead has a contactId
      const lead = leads.find((l) => l.id === id) || selectedLead;
      if (lead?.contactId) {
        api.post('/lead-capture/push-telecaller', {
          contactId: lead.contactId,
        }).catch(() => { /* best-effort */ });
      }

      setTeleCrmConfirmedIds((prev) => new Set(prev).add(id));
      setSelectedLead((l) => l ? {
        ...l,
        notes: `${l.notes ? `${l.notes}\n` : ''}[AURA Sales] Pushed at ${new Date().toISOString()}`,
      } : null);
      fetchLeads();
      alert('Lead pushed to AURA Sales App successfully.');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to push to AURA Sales App');
    } finally { setPushingTeleCrm(false); }
  };

  const handleWhatsAppVerify = async (lead: Lead) => {
    if (!lead.phone) {
      alert('Phone number is required for WhatsApp verification.');
      return;
    }
    try {
      // Use the new lead-capture verify-number API (ChatKart integration)
      if (lead.contactId) {
        const verifyRes = await api.post('/lead-capture/verify-number', {
          contactId: lead.contactId,
          phone: lead.phone,
        });
        const verifyData = verifyRes.data?.data;
        const status = verifyData?.verificationStatus || 'pending';
        const marker = `[ChatKart] WA verification ${verifyData?.sent ? 'sent' : 'triggered'} (${status}) at ${new Date().toISOString()}`;
        await api.patch(`/lead-integrations/lead-imports/${lead.id}/details`, {
          notes: `${lead.notes ? `${lead.notes}\n` : ''}${marker}`,
        });
        setSelectedLead((l) => l ? { ...l, notes: `${l.notes ? `${l.notes}\n` : ''}${marker}` } : null);
        fetchLeads();
        alert(verifyData?.sent ? 'WhatsApp verification message sent via ChatKart.' : 'WhatsApp verification triggered (stub mode — configure CHATKART_API_KEY for real messages).');
      } else {
        // Fallback: trigger via lead-automation config test for unprocessed leads
        await api.post('/lead-automation/config/test', { phone: lead.phone }).catch(() => null);
        const marker = `[ChatKart] Verification triggered at ${new Date().toISOString()}`;
        await api.patch(`/lead-integrations/lead-imports/${lead.id}/details`, {
          notes: `${lead.notes ? `${lead.notes}\n` : ''}${marker}`,
        });
        setSelectedLead((l) => l ? { ...l, notes: `${l.notes ? `${l.notes}\n` : ''}${marker}` } : null);
        fetchLeads();
        alert('WhatsApp verification triggered. Process the lead first for full ChatKart integration.');
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to trigger WhatsApp verification');
    }
  };

  /* ---- Mark Dead / Lost / Won ---- */
  // 2026-10-03: Lost and Dead go through POST /telecaller/leads/:id/mark-lost —
  // the phone's path (lead_lost_action.dart): stage engine + loss audit row,
  // 'dead' is one of its reasons. handleStageChange's PATCH .../stage is
  // refused 403 manual_stage_change_disabled for every caller without _source.
  const markLeadLost = async (id: string, reason: 'dead' | 'not_interested'): Promise<boolean> => {
    try {
      await api.post(`/telecaller/leads/${id}/mark-lost`, { reason });
      fetchLeads(); fetchDashStats();
      return true;
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to mark the lead lost');
      return false;
    }
  };

  const handleMarkDead = async (id: string) => {
    if (!confirm("Mark this lead as Dead (can't reach after multiple attempts)?")) return;
    if (!(await markLeadLost(id, 'dead'))) return;
    // Also mark lost on the deal via lead-capture
    const lead = leads.find((l) => l.id === id) || selectedLead;
    if (lead?.dealId) {
      api.post(`/lead-capture/${lead.dealId}/mark-lost`, { reason: 'no_response' }).catch(() => {});
    }
    if (selectedLead?.id === id) setSelectedLead(null);
  };

  const handleMarkLost = async (id: string, reason: 'not_interested' = 'not_interested') => {
    if (!confirm('Mark this lead as Lost?')) return;
    if (!(await markLeadLost(id, reason))) return;
    // Also mark lost on the deal via lead-capture
    const lead = leads.find((l) => l.id === id) || selectedLead;
    if (lead?.dealId) {
      api.post(`/lead-capture/${lead.dealId}/mark-lost`, { reason }).catch(() => {});
    }
    if (selectedLead?.id === id) setSelectedLead(null);
  };

  // 2026-10-03 (Manak): Mark Won ends at a sales order. It opens the
  // convert-to-SO dialog (POST /sales-orders/from-proposal/:id, the phone's
  // path); the order's confirm cascade advances the lead. No stage PATCH and no
  // /lead-capture mark-won — both were a Won with no order behind it.
  const handleMarkWon = (id: string) => {
    const lead = leads.find((l) => l.id === id) || selectedLead;
    if (lead) setMarkWonLead({ id: lead.id, name: lead.name || 'this lead' });
  };

  /* ---- Interest Level ---- */
  const handleInterestLevel = async (id: string, level: 'hot' | 'warm' | 'cold') => {
    try {
      await api.patch(`/lead-integrations/lead-imports/${id}/details`, { interestLevel: level });
      setSelectedLead((l) => l ? { ...l, interestLevel: level } : null);
      setLeads((prev) => prev.map((l) => l.id === id ? { ...l, interestLevel: level } : l));
    } catch { /* silent */ }
  };

  /* ---- Add Lead ---- */
  // Lookup collaborator phone when user finishes typing (blur)
  const lookupRefPhone = async (idx: number, phone: string) => {
    if (!phone || phone.length < 10) { setRefPhoneLookup((p) => ({ ...p, [idx]: null })); return; }
    try {
      const res = await api.post('/collaborators/lookup', { phone });
      if (res.data.data) {
        setRefPhoneLookup((p) => ({ ...p, [idx]: { found: true, name: `${joinPersonName(res.data.data.firstName, res.data.data.lastName)}` } }));
      } else {
        setRefPhoneLookup((p) => ({ ...p, [idx]: { found: false } }));
      }
    } catch { setRefPhoneLookup((p) => ({ ...p, [idx]: null })); }
  };

  // Load existing contacts for customer reference picker
  const loadContactsForRef = async () => {
    if (existingContacts.length > 0) return;
    try {
      const res = await api.get('/contacts?limit=200&sortBy=firstName&sortOrder=asc');
      setExistingContacts(res.data.data?.map((c: any) => ({ id: c.id, firstName: c.firstName, lastName: c.lastName, phone: c.phone })) || []);
    } catch {}
  };

  const handleAddLead = async () => {
    if (!leadForm.name.trim()) return;

    // Validate reference phone numbers
    for (const r of refs) {
      if (r.type !== 'customer' && r.type) {
        if (!r.phone || r.phone.replace(/\D/g, '').length < 10) {
          alert(`Phone number is required for ${r.type} reference`);
          return;
        }
        if (!r.name.trim()) {
          alert(`Name is required for ${r.type} reference`);
          return;
        }
      }
      if (r.type === 'customer' && !r.customerId) {
        alert('Please select an existing customer for the customer reference');
        return;
      }
    }

    setSavingLead(true);
    setCreatedNotice(null);
    try {
      // Source is always 'manual' for manual adds
      let sourceId = sources.find((s) => s.name.toLowerCase() === 'manual')?.id;
      if (!sourceId) {
        const srcRes = await api.post('/lead-integrations/lead-sources', { name: 'manual', type: 'manual' });
        sourceId = srcRes.data.data?.id;
      }

      const leadRes = await api.post('/lead-integrations/lead-imports', {
        sourceId, sourceName: 'manual',
        name: leadForm.name.trim(), company: leadForm.company || null,
        email: leadForm.email || null, phone: leadForm.phone || null,
        product: leadForm.product || null,
        location: leadForm.location || null, message: leadForm.message || null,
        pincode: /^\d{6}$/.test(leadForm.pincode) ? leadForm.pincode : null,
        priority: leadForm.priority, interestLevel: leadForm.interestLevel,
        requirement: leadForm.requirement || null,
      });

      // Tag references if any — use the contact that gets created from the lead import
      const leadId = leadRes.data.data?.id;
      const contactId = leadRes.data.data?.contactId;
      if (contactId && refs.length > 0) {
        for (const r of refs) {
          if (!r.type) continue;
          try {
            if (r.type === 'customer') {
              await api.post('/collaborators/referrals', {
                contactId, referralType: 'customer', referredByContactId: r.customerId,
              });
            } else {
              await api.post('/collaborators/referrals', {
                contactId, referralType: r.type,
                collaboratorPhone: r.phone, collaboratorName: r.name.trim(),
                collaboratorLastName: r.lastName.trim() || undefined,
                collaboratorEmail: r.email || undefined,
              });
            }
          } catch (refErr: any) {
            console.warn('Referral tagging failed:', refErr.response?.data?.error);
          }
        }
      }

      const createdName = leadForm.name.trim();
      setShowAddLead(false);
      setLeadForm({ sourceName: 'manual', name: '', company: '', email: '', phone: '', product: '', quantity: '', location: '', locationState: '', pincode: '', message: '', priority: 'medium', interestLevel: 'warm', requirement: '' });
      setPinLookup({ loading: false, found: null, label: '' });
      setRefs([]); setRefPhoneLookup({});
      // The new lead is at the top of page 1 of "All" and nowhere else, so go
      // back there rather than re-reading whatever page/tab/search the user was
      // standing on — that re-read is why the row "needed a browser refresh".
      // 🔒 The reset below is done FOR the rep, so she is told what it cost.
      // Wiping a search she typed without a word is how "where did my filter
      // go?" becomes "the app is broken"; the stage tab reads the same way.
      const clearedBits: string[] = [];
      if (search.trim() !== '') clearedBits.push(`the search for "${search.trim()}"`);
      if (activeTab !== 'all') clearedBits.push('the stage filter');
      const clearedNote = clearedBits.length === 0
        ? null
        : `${createdName} was added. ${clearedBits.join(' and ')} ${clearedBits.length === 1 ? 'was' : 'were'} cleared so you can see it.`;
      if (leadId) {
        pendingCreatedRef.current = { id: leadId, name: createdName, clearedNote };
        lastCreatedRef.current = { id: leadId, name: createdName };
      }
      const viewAlreadyShowsNewest = page === 1 && activeTab === 'all' && search === '';
      setSearch(''); setActiveTab('all'); setPage(1);
      // Those setters re-run the list effect on their own; fetch by hand only
      // when nothing changed, so a create never fires two list requests.
      if (viewAlreadyShowsNewest) fetchLeads();
      fetchDashStats();
      // Success → offer to assign the new lead (rep or dealer) via the triage
      // picker. leadId is the LeadImport id, which /leads/:id/assign-* accepts.
      if (leadId) setAssignFor({ id: leadId, name: createdName });
    } catch (err: any) {
      const data = err.response?.data;
      // The server refuses a duplicate mobile with 409 DUPLICATE_LEAD whose body
      // carries `message` + existing ids — NOT `error`. Reading only `.error`
      // (the old code) swallowed the real reason and showed "Failed to add lead".
      if (err.response?.status === 409 && data?.code === 'DUPLICATE_LEAD') {
        const owner = data.assignedToName ? ` — currently with ${data.assignedToName}` : '';
        const digits = (leadForm.phone || '').replace(/\D/g, '');
        if (window.confirm(`A lead with this number already exists${owner}.\n\nOpen the existing lead?`)) {
          setShowAddLead(false);
          setSearch(digits.length >= 10 ? digits.slice(-10) : (leadForm.phone || ''));
        }
        return;
      }
      alert(data?.message || data?.error || 'Failed to add lead');
    } finally { setSavingLead(false); }
  };

  /* ---- Post-create assignment (reuses the triage picker + endpoints) ---- */
  // Lazy-load the roster + dealer groups when the picker opens.
  useEffect(() => {
    if (!assignFor) return;
    setAssignMembersLoading(true);
    api.get('/leads/triage/assignable-members')
      .then((r) => setAssignMembers((r.data?.data as AssignableMember[]) ?? []))
      .catch(() => setAssignMembers([]))
      .finally(() => setAssignMembersLoading(false));
    setAssignDealersLoading(true);
    fetch('/api/v1/leads/triage/assignable-dealers', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((j) => setAssignDealerGroups(Array.isArray(j?.groups) ? (j.groups as AssignableDealerGroup[]) : []))
      .catch(() => setAssignDealerGroups([]))
      .finally(() => setAssignDealersLoading(false));
  }, [assignFor]);

  const closeAssign = () => {
    setAssignFor(null);
    setAssignMembers(null); setAssignDealerGroups(null);
  };

  async function submitLeadMemberAssign(member: AssignableMember) {
    if (!assignFor) return;
    setAssignBusy(true);
    try {
      const res = await fetch(`/api/v1/leads/${assignFor.id}/assign-team`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assignBodyForSelection(member)), credentials: 'include',
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.message || j?.error || `HTTP ${res.status}`);
      const who = j?.data?.mode === 'executive'
        ? j.data.assignedToName
        : `${j?.data?.assignedToName ?? 'Manager'}${j?.data?.coordinatorName ? ' + ' + j.data.coordinatorName : ''}`;
      setAssignFlash(`Assigned to ${who} (${member.teamName})`);
      setTimeout(() => setAssignFlash(''), 5000);
      closeAssign();
      fetchLeads(); fetchDashStats();
    } catch (e) {
      alert(`Failed to assign: ${(e as Error).message}`);
    } finally { setAssignBusy(false); }
  }

  async function submitLeadDealerAssign(dealer: AssignableDealer) {
    if (!assignFor) return;
    setAssignBusy(true);
    try {
      const res = await fetch(`/api/v1/leads/${assignFor.id}/assign-dealer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dealerId: dealer.id }), credentials: 'include',
      });
      const j = await res.json().catch(() => ({}));
      // A dealer is PARALLEL to a rep: a MANAGER's dealer needs a rep owner
      // first. A company-owned dealer may take a lead with no rep (2026-10-05).
      // The server decides and words the refusal; show its message verbatim.
      if (res.status === 422 && j?.error === 'NO_EXEC_OWNER') {
        alert(j?.message || 'Assign a sales member first, then the dealer.');
        return;
      }
      if (!res.ok) throw new Error(j?.message || j?.error || `HTTP ${res.status}`);
      const name: string = j?.data?.assignedDealer?.name ?? dealer.name;
      setAssignFlash(`Assigned to dealer ${name}`);
      setTimeout(() => setAssignFlash(''), 5000);
      closeAssign();
      fetchLeads(); fetchDashStats();
    } catch (e) {
      alert(`Failed to assign dealer: ${(e as Error).message}`);
    } finally { setAssignBusy(false); }
  }

  /* ---- CSV / Excel Import ---- */

  // 2026-10-07: parsing, header aliases, skip rules and the Manual default
  // live in leadFileImport.ts (unit-tested); the preview + Import button in
  // LeadFileImportPreview. Nothing is saved until the user clicks Import.
  const downloadSampleCsv = () => {
    const url = URL.createObjectURL(new Blob([sampleCsv()], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'aura-lead-import-sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleLeadFileChosen = async (file: File) => {
    setImportingCsv(true);
    setFileImport(null);
    try {
      const isExcel = /\.(xlsx|xls)$/i.test(file.name);
      const table = isExcel
        ? parseWorkbook(await import('xlsx'), await file.arrayBuffer())
        : parseCsvText(await file.text());
      if (table.length < 2) { alert('The file needs a header row and at least one data row.'); return; }
      setFileImport({ fileName: file.name, table, columns: buildColumns(table) });
    } catch (err: any) {
      alert(`Could not read ${file.name}: ${err?.message || 'unknown error'}`);
    } finally { setImportingCsv(false); }
  };

  /* ---- Activity Logging ---- */
  const fetchActivities = async (leadId: string) => {
    setLoadingActivities(true);
    try {
      const res = await api.get(`/lead-integrations/lead-imports/${leadId}/activities`);
      setActivities(res.data.data ?? []);
    } catch { setActivities([]); }
    finally { setLoadingActivities(false); }
  };

  const fetchLeadImages = async (leadId: string) => {
    setLoadingImages(true);
    try {
      const res = await api.get(`/files/by-entity/leadImport/${leadId}`);
      setLeadImages((res.data.data ?? []).filter((f: any) => f.mimeType?.startsWith('image/')));
    } catch { setLeadImages([]); }
    finally { setLoadingImages(false); }
  };

  const handleLogActivity = async () => {
    if (!selectedLead || !activityForm.title.trim()) return;
    setLoggingActivity(true);
    try {
      await api.post(`/lead-integrations/lead-imports/${selectedLead.id}/activities`, {
        type: activityForm.type,
        title: activityForm.title.trim(),
        description: activityForm.description || undefined,
      });
      setActivityForm({ type: 'call', title: '', description: '' });
      fetchActivities(selectedLead.id);
      // Also append to notes
      const marker = `[${activityForm.type}] ${activityForm.title.trim()}`;
      await api.patch(`/lead-integrations/lead-imports/${selectedLead.id}/details`, {
        notes: (detailNotes ? detailNotes + '\n' : '') + marker,
      });
      setDetailNotes((prev) => (prev ? prev + '\n' : '') + marker);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to log activity');
    } finally { setLoggingActivity(false); }
  };

  /* ---- Lead Sources ---- */
  const handleAddSource = async () => {
    if (!canManageSources || !sourceForm.name.trim()) return;
    setSavingSource(true);
    try {
      let config: any = {};
      try { config = JSON.parse(sourceForm.config); } catch { alert('Invalid JSON in config'); setSavingSource(false); return; }
      await api.post('/lead-integrations/lead-sources', { name: sourceForm.name.trim(), type: sourceForm.type, config });
      setShowAddSource(false);
      setSourceForm({ name: '', type: 'api', config: '{}' });
      fetchSources();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to add source');
    } finally { setSavingSource(false); }
  };

  const toggleSourceActive = async (source: LeadSource) => {
    if (!canManageSources) return;
    try {
      await api.put(`/lead-integrations/lead-sources/${source.id}`, { isActive: !source.isActive });
      fetchSources();
    } catch (err: any) { alert(err.response?.data?.error || 'Failed to update source'); }
  };

  const deleteSource = async (source: LeadSource) => {
    if (!canManageSources) return;
    if (!confirm(`Delete source "${source.name}"?`)) return;
    try {
      await api.delete(`/lead-integrations/lead-sources/${source.id}`);
      fetchSources();
    } catch (err: any) { alert(err.response?.data?.error || 'Failed to delete source'); }
  };

  /* ---- AI Lead Scoring ---- */
  // Now uses /ai/lead-score/import which works directly on a LeadImport — no contact
  // conversion required (Kunwar Bug 6). Also surfaces explicit re-score feedback
  // (Kunwar Bug 13) via toast-style alert with old→new delta.
  const handleScoreLead = async (lead: Lead) => {
    const prevScore = lead.score ?? null;
    setScoringOne(lead.id);
    try {
      let score: number;
      let breakdown: Record<string, number> | undefined;
      // Prefer new endpoint that works on LeadImport directly
      try {
        const res = await api.post('/ai/lead-score/import', { leadImportId: lead.id });
        const payload = res.data.data ?? res.data;
        score = payload.score;
        breakdown = payload.breakdown;
      } catch (newErr: any) {
        // Fallback for older backend: requires contactId
        if (!lead.contactId) throw newErr;
        const res = await api.post('/ai/lead-score', { contactId: lead.contactId });
        score = (res.data.data ?? res.data).score;
        await api.patch(`/lead-integrations/lead-imports/${lead.id}/score`, { score });
      }
      setLeads((prev) => prev.map((l) => l.id === lead.id ? { ...l, score } : l));
      if (selectedLead?.id === lead.id) setSelectedLead((l) => l ? { ...l, score } : null);
      // Feedback toast — shows delta on re-score
      const delta = prevScore != null ? (score - prevScore) : null;
      const deltaText = delta != null ? ` (was ${prevScore}, ${delta >= 0 ? '+' : ''}${delta})` : '';
      alert(`AI Score: ${score}/100${deltaText}${breakdown ? '\n\nTop signals:\n' + Object.entries(breakdown).filter(([,v]) => v > 0).map(([k,v]) => `• ${k}: +${v}`).join('\n') : ''}`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to score lead');
    } finally { setScoringOne(null); }
  };

  const handleScoreAllLeads = async () => {
    // Now scores any lead (not just processed-with-contact) via the new endpoint
    const scorable = leads;
    if (scorable.length === 0) { alert('No leads to score.'); return; }
    setScoringAll(true);
    setScoreProgress({ done: 0, total: scorable.length });
    for (let i = 0; i < scorable.length; i++) {
      try {
        const res = await api.post('/ai/lead-score/import', { leadImportId: scorable[i].id });
        const score = (res.data.data ?? res.data).score;
        setLeads((prev) => prev.map((l) => l.id === scorable[i].id ? { ...l, score } : l));
      } catch {
        // Fallback to contact-based scoring if old backend is still live
        try {
          if (scorable[i].contactId) {
            const res = await api.post('/ai/lead-score', { contactId: scorable[i].contactId });
            const score = (res.data.data ?? res.data).score;
            await api.patch(`/lead-integrations/lead-imports/${scorable[i].id}/score`, { score });
            setLeads((prev) => prev.map((l) => l.id === scorable[i].id ? { ...l, score } : l));
          }
        } catch { /* skip */ }
      }
      setScoreProgress({ done: i + 1, total: scorable.length });
      if (i < scorable.length - 1) await new Promise((r) => setTimeout(r, 300));
    }
    setScoringAll(false);
  };

  /* ---- Selection helpers ---- */
  const selectableLeads = leads.filter((l) => l.status === 'new');
  const allSelected = selectableLeads.length > 0 && selectableLeads.every((l) => selected.has(l.id));
  const toggleSelectAll = () => { if (allSelected) setSelected(new Set()); else setSelected(new Set(selectableLeads.map((l) => l.id))); };
  const toggleSelect = (id: string) => { setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };

  const openDetail = (lead: Lead) => {
    setSelectedLead(lead); setDetailNotes(lead.notes || ''); setShowCallLog(false); fetchLeadImages(lead.id);
    setShowEditContact(false); setEditContactError(null); // WEB-EDIT: reset admin edit form per-lead
    // Load interaction history
    if (lead.contactId) {
      setLoadingInteractions(true);
      api.get(`/sales-teams/interactions/${lead.contactId}`)
        .then(r => setInteractions(r.data.data || []))
        .catch(() => setInteractions([]))
        .finally(() => setLoadingInteractions(false));
    } else {
      setInteractions([]);
    }
  };

  /* ---- Computed ---- */
  const totalPages = meta.totalPages || Math.max(1, Math.ceil(meta.total / PAGE_SIZE));

  const stageBadge = (stage: string) => {
    const s = STAGE_BADGE[stage] ?? STAGE_BADGE.new;
    const stageLabel = LEAD_STAGES.find((ls) => ls.value === stage)?.label ?? stage;
    return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${s.bg} ${s.text}`}>{stageLabel}</span>;
  };

  const scoreBadge = (score?: number) => {
    if (score == null) return <span className="text-xs text-[var(--text-muted)]">-</span>;
    const cls = score >= 70 ? 'bg-green-500/10 text-green-400 border-green-500/20' : score >= 40 ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20';
    const label = score >= 70 ? 'Hot' : score >= 40 ? 'Warm' : 'Cold';
    return (
      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-bold ${cls}`}>
        {score}<span className="text-[9px] font-normal opacity-70">{label}</span>
      </span>
    );
  };

  const phoneStatusBadge = (status?: string) => {
    if (!status) return null;
    const map: Record<string, { icon: any; text: string; color: string }> = {
      valid:          { icon: CheckCircle, text: 'Valid', color: 'text-green-400' },
      not_functional: { icon: AlertTriangle, text: 'Not Functional', color: 'text-orange-400' },
      incorrect:      { icon: Ban, text: 'Incorrect', color: 'text-red-400' },
    };
    const s = map[status];
    if (!s) return null;
    return <span className={`flex items-center gap-1 text-xs ${s.color}`}><s.icon size={10} />{s.text}</span>;
  };

  // KPI summary for header stats — T12: from stageCounts/meta.total (the
  // /leads-list response), not dashStats, so header/tabs/rows never disagree.
  const wonCount = stageCounts['won'] ?? 0;
  const deadCount = stageCounts['dead'] ?? 0;
  const lostCount = stageCounts['lost'] ?? 0;
  const inPipelineCount = bookTotal - wonCount - deadCount - lostCount;
  const selectedOwner = ownerBreakdown.owners.find((owner) => owner.userId === filterOwner);
  const selectedOwnerLabel = filterOwner === 'unassigned'
    ? `Unassigned · ${ownerBreakdown.unassignedCount}`
    : filterOwner
      ? `${selectedOwner?.name ?? filterOwnerName} · ${selectedOwner?.count ?? 0}`
      : '';

  const handleOwnerFilterChange = (nextOwner: string) => {
    if (nextOwner && nextOwner !== 'unassigned') {
      setFilterOwnerName(ownerBreakdown.owners.find((owner) => owner.userId === nextOwner)?.name ?? filterOwnerName);
    } else if (!nextOwner) {
      setFilterOwnerName('');
    }
    setFilterOwner(nextOwner);
    setPage(1);
    setSelected(new Set());
  };

  const handleStageTabChange = (nextStage: string) => {
    setActiveTab(nextStage);
    if (nextStage !== 'lost') setFilterLostReason('');
    setPage(1);
    setSelected(new Set());
  };

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  return (
    <div className="relative">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500">
            <TrendingUp size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">Sales Pipeline</h1>
            <p className="text-sm text-[var(--text-muted)]">Lead capture · qualification · conversion</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div data-tour="leads-view-toggle" className="flex items-center rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] p-0.5">
            {/* BUG-23 fix (May 1 2026): list/board toggle uses warm-ink (forest) instead of bright indigo */}
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-[var(--ink-0)] text-[var(--ivory-0)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
            >
              <LayoutList size={14} /> List
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${viewMode === 'kanban' ? 'bg-[var(--ink-0)] text-[var(--ivory-0)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
            >
              <Columns size={14} /> Board
            </button>
          </div>
          <LeadExportButton filters={leadFilters} />
          {/* BUG-23 fix: Score All uses saffron instead of purple */}
          <button
            onClick={handleScoreAllLeads}
            disabled={scoringAll}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white disabled:opacity-50 transition-colors"
            style={{ background: 'var(--saffron, #C98A3B)' }}
          >
            {scoringAll ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {scoringAll ? `${scoreProgress.done}/${scoreProgress.total}` : 'Score All'}
          </button>
          {/* BUG-23 fix: Add Lead uses ink-0 (warm dark) instead of indigo-purple gradient */}
          <button
            onClick={() => setShowAddLead(true)}
            className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all"
            style={{ background: 'var(--ink-0)', color: 'var(--ivory-0)' }}
          >
            <Plus size={16} /> Add Lead
          </button>
          {/* AI-fc37 (2026-07-28) — Field-Activity holding board (field leads
              still chasing a decision-maker number; hidden from this pipeline). */}
          <Link
            to="/field-activity"
            className="flex items-center gap-2 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-inset)]"
            title="Field leads awaiting a decision-maker number"
          >
            <span style={{ display: 'inline-flex', gap: 3 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#D5433B' }} />
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#E0A02E' }} />
            </span>
            Field Activity
          </Link>
          <button
            onClick={() => setShowImportPanel(!showImportPanel)}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${showImportPanel ? 'border-indigo-500/50 bg-indigo-500/10 text-indigo-400' : 'border-[var(--border-primary)] bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]'}`}
          >
            <Download size={16} /> Import
          </button>
          <button
            onClick={() => setShowSources(!showSources)}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${showSources ? 'border-indigo-500/50 bg-indigo-500/10 text-indigo-400' : 'border-[var(--border-primary)] bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]'}`}
          >
            <Settings size={16} /> Sources
          </button>
          <button
            onClick={() => { fetchLeads(); fetchDashStats(); }}
            className="flex items-center rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] p-2 text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] transition-colors"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Dashboard KPI Cards */}
      {/* KPI cards now derive from bookTotal/stageCounts (the /leads-list response),
          not dashStats — so don't hide the whole row when the separate dashStats
          fetch fails. Show once the leads book has loaded, or if dashStats exists. */}
      {(dashStats || bookTotal > 0 || Object.keys(stageCounts).length > 0) && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          <KPICard label="Total" value={bookTotal} color="indigo" />
          <KPICard label="In Pipeline" value={inPipelineCount} color="blue" />
          <KPICard label="New Leads" value={stageCounts['new'] || 0} color="cyan" />
          <KPICard label="Follow-up" value={(stageCounts['follow_up'] || 0) + (stageCounts['first_contact'] || 0)} color="sky" />
          <KPICard label="Quoted" value={(stageCounts['quotation_sent'] || 0) + (stageCounts['revised_quotation'] || 0)} color="purple" />
          <KPICard label="Negotiation" value={stageCounts['negotiation'] || 0} color="orange" />
          <KPICard label="Won" value={wonCount} color="green" />
          <KPICard label="Lost / Dead" value={lostCount + deadCount} color="red" />
        </div>
      )}

      {/* Alert banners */}
      {dashStats && (dashStats.overdueFollowUps > 0 || dashStats.unassigned > 0) && (
        <div className="mb-4 flex flex-wrap gap-3">
          {dashStats.overdueFollowUps > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-orange-500/30 bg-orange-500/5 px-4 py-2 text-sm">
              <Clock size={16} className="text-orange-400" />
              <span className="text-orange-400 font-medium">{dashStats.overdueFollowUps} overdue follow-ups</span>
            </div>
          )}
          {dashStats.unassigned > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-4 py-2 text-sm">
              <UserPlus size={16} className="text-yellow-400" />
              <span className="text-yellow-400 font-medium">{dashStats.unassigned} unassigned leads</span>
            </div>
          )}
        </div>
      )}

      {/* AI Scoring Progress */}
      {scoringAll && (
        <div className="mb-4 rounded-lg border border-purple-500/20 bg-purple-500/5 p-3">
          <div className="mb-2 flex items-center justify-between text-xs text-purple-400">
            <span className="flex items-center gap-1.5"><Sparkles size={12} /> Scoring leads with AI...</span>
            <span>{scoreProgress.done} / {scoreProgress.total}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-purple-500/10">
            <div className="h-full rounded-full bg-purple-500 transition-all duration-300" style={{ width: `${scoreProgress.total > 0 ? (scoreProgress.done / scoreProgress.total) * 100 : 0}%` }} />
          </div>
        </div>
      )}

      {/* Add Lead Modal */}
      {showAddLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowAddLead(false)}>
          <div className="w-full max-w-xl rounded-2xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6 shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="mb-4 text-lg font-semibold text-[var(--text-primary)]">Add New Lead</h2>
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Name *" value={leadForm.name} onChange={(v) => setLeadForm((f) => ({ ...f, name: v }))} placeholder="Full name" />
              <InputField label="Company" value={leadForm.company} onChange={(v) => setLeadForm((f) => ({ ...f, company: v }))} placeholder="Company name" />
              <InputField label="Email" value={leadForm.email} onChange={(v) => setLeadForm((f) => ({ ...f, email: v }))} placeholder="email@example.com" />
              <InputField label="Phone" value={leadForm.phone} onChange={(v) => setLeadForm((f) => ({ ...f, phone: v }))} placeholder="+91..." />
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Product Interest *</label>
                <select
                  value={leadForm.product}
                  onChange={(e) => setLeadForm((f) => ({ ...f, product: e.target.value }))}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none"
                >
                  <option value="">Select product category...</option>
                  {leadFormCategories.map((c: any) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Pincode</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={6}
                  value={leadForm.pincode}
                  onChange={(e) => handlePincode(e.target.value)}
                  placeholder="6-digit pincode"
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none"
                />
                {pinLookup.loading && <p className="mt-1 text-xs text-[var(--text-muted)]">Looking up…</p>}
                {pinLookup.found === true && <p className="mt-1 text-xs text-green-400">✓ {pinLookup.label}</p>}
                {pinLookup.found === false && <p className="mt-1 text-xs text-amber-400">{pinLookup.label}</p>}
              </div>
              <InputField
                label={leadForm.locationState ? `City (${leadForm.locationState})` : 'City / Location'}
                value={leadForm.location}
                onChange={(v) => setLeadForm((f) => ({ ...f, location: v }))}
                placeholder="Auto-fills from pincode"
              />
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Priority</label>
                <select value={leadForm.priority} onChange={(e) => setLeadForm((f) => ({ ...f, priority: e.target.value }))} className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none">
                  {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Interest Level</label>
                <div className="flex gap-2">
                  {INTEREST_LEVELS.map((il) => (
                    <button key={il.value} type="button"
                      onClick={() => setLeadForm((f) => ({ ...f, interestLevel: il.value }))}
                      className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors ${leadForm.interestLevel === il.value ? `${il.bg} ${il.text} border-current` : 'border-[var(--border-primary)] text-[var(--text-muted)]'}`}
                    >
                      {il.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ---- References / Collaborators ---- */}
            <div className="mt-4 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] p-3">
              <div className="mb-2 flex items-center justify-between">
                <label className="text-xs font-semibold text-[var(--text-primary)]">References (Optional)</label>
                <button
                  type="button"
                  onClick={() => { setRefs((r) => [...r, { type: '', name: '', lastName: '', phone: '', email: '', customerId: '' }]); loadContactsForRef(); }}
                  className="flex items-center gap-1 rounded-md bg-indigo-600/10 px-2 py-1 text-xs font-medium text-indigo-400 hover:bg-indigo-600/20"
                >
                  <Plus size={12} /> Add Reference
                </button>
              </div>
              {refs.length === 0 && (
                <p className="text-xs text-[var(--text-muted)]">No references tagged. Lead can be created without references.</p>
              )}
              {refs.map((r, idx) => {
                const usedTypes = refs.map((x, i) => i !== idx ? x.type : '').filter(Boolean);
                return (
                  <div key={idx} className="mb-3 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-medium text-[var(--text-muted)]">Reference #{idx + 1}</span>
                      <button type="button" onClick={() => { setRefs((prev) => prev.filter((_, i) => i !== idx)); }} className="text-red-400 hover:text-red-300"><X size={14} /></button>
                    </div>
                    <div className="mb-2">
                      <label className="mb-1 block text-xs text-[var(--text-muted)]">Type *</label>
                      <select
                        value={r.type}
                        onChange={(e) => setRefs((prev) => prev.map((x, i) => i === idx ? { ...x, type: e.target.value, customerId: '' } : x))}
                        className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none"
                      >
                        <option value="">Select type...</option>
                        {[
                          { value: 'plumber', label: 'Plumber' },
                          { value: 'architect', label: 'Architect' },
                          { value: 'dealer', label: 'Dealer' },
                          { value: 'freelancer', label: 'Other / Freelancer' },
                          { value: 'customer', label: 'Existing Customer' },
                        ].filter((t) => !usedTypes.includes(t.value)).map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    {r.type === 'customer' ? (
                      <div>
                        <label className="mb-1 block text-xs text-[var(--text-muted)]">Select Customer *</label>
                        <select
                          value={r.customerId}
                          onChange={(e) => setRefs((prev) => prev.map((x, i) => i === idx ? { ...x, customerId: e.target.value } : x))}
                          className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none"
                        >
                          <option value="">Pick a customer...</option>
                          {existingContacts.map((c) => (
                            <option key={c.id} value={c.id}>{joinPersonName(c.firstName, c.lastName)}{c.phone ? ` (${c.phone})` : ''}</option>
                          ))}
                        </select>
                      </div>
                    ) : r.type ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="mb-1 block text-xs text-[var(--text-muted)]">Name *</label>
                            <input type="text" value={r.name} onChange={(e) => setRefs((prev) => prev.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} placeholder="First name" className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none" />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs text-[var(--text-muted)]">Last Name</label>
                            <input type="text" value={r.lastName} onChange={(e) => setRefs((prev) => prev.map((x, i) => i === idx ? { ...x, lastName: e.target.value } : x))} placeholder="Last name" className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none" />
                          </div>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs text-[var(--text-muted)]">Phone * <span className="text-[var(--text-muted)]">(+91)</span></label>
                          <div className="flex items-center gap-1">
                            <span className="rounded-l-lg border border-r-0 border-[var(--border-primary)] bg-[var(--bg-inset)] px-2 py-2 text-xs text-[var(--text-muted)]">+91</span>
                            <input
                              type="tel"
                              maxLength={10}
                              value={r.phone}
                              onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); setRefs((prev) => prev.map((x, i) => i === idx ? { ...x, phone: v } : x)); }}
                              onBlur={() => lookupRefPhone(idx, r.phone)}
                              placeholder="10-digit number"
                              className="w-full rounded-r-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none"
                            />
                          </div>
                          {refPhoneLookup[idx]?.found && (
                            <p className="mt-1 text-xs text-green-400">✓ Found: {refPhoneLookup[idx]?.name} — existing collaborator will be tagged</p>
                          )}
                          {refPhoneLookup[idx] && !refPhoneLookup[idx]?.found && r.phone.length === 10 && (
                            <p className="mt-1 text-xs text-amber-400">New collaborator — will be created on save</p>
                          )}
                        </div>
                        <div>
                          <label className="mb-1 block text-xs text-[var(--text-muted)]">Email</label>
                          <input type="email" value={r.email} onChange={(e) => setRefs((prev) => prev.map((x, i) => i === idx ? { ...x, email: e.target.value } : x))} placeholder="Optional" className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none" />
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div className="mt-3">
              <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Requirement / Message</label>
              <textarea
                value={leadForm.requirement || leadForm.message}
                onChange={(e) => setLeadForm((f) => ({ ...f, requirement: e.target.value, message: e.target.value }))}
                rows={2}
                className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-indigo-500 focus:outline-none"
                placeholder="Describe the lead's requirement..."
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setShowAddLead(false)} className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]">Cancel</button>
              <button
                onClick={handleAddLead}
                disabled={savingLead || !leadForm.name.trim() || !leadForm.product}
                className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {savingLead ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Add Lead
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-create assignment — reuses the triage picker (sales members + dealers) */}
      {assignFor && (
        <TriageMemberPicker
          mode="assign"
          leadName={assignFor.name || null}
          members={assignMembers}
          loading={assignMembersLoading}
          busy={assignBusy}
          onClose={() => !assignBusy && closeAssign()}
          onPick={(m) => void submitLeadMemberAssign(m)}
          dealerGroups={assignDealerGroups}
          dealersLoading={assignDealersLoading}
          pickDealer={(d) => void submitLeadDealerAssign(d)}
        />
      )}

      {/* Assign success flash */}
      {assignFlash && (
        <div className="fixed bottom-4 right-4 z-[60] rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-lg">
          {assignFlash}
        </div>
      )}

      {/* Import Panel */}
      {showImportPanel && (
        <div className="mb-6 rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">Import Leads</h2>
            <button onClick={() => setShowImportPanel(false)} className="rounded-lg p-1 text-[var(--text-muted)] hover:bg-[var(--bg-inset)]"><X size={18} /></button>
          </div>
          <p className="mb-4 text-sm text-[var(--text-muted)]">Leads are imported automatically from configured sources. You can also add leads manually or upload a CSV.</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-5">
            {[
              { title: 'IndiaMART', desc: 'Auto-receives leads from IndiaMART enquiries.', badge: 'Auto-import', color: 'orange' },
              { title: 'WhatsApp', desc: 'Capture leads from WhatsApp Business API.', badge: 'Integration', color: 'green' },
              { title: 'API', desc: 'Push leads programmatically via REST API.', badge: 'Developer', color: 'blue' },
              { title: 'Manual', desc: 'Add leads from trade shows, calls, or walk-ins.', badge: 'Manual', color: 'gray', action: () => { setShowImportPanel(false); setShowAddLead(true); } },
            ].map((item) => (
              <div key={item.title} className="rounded-lg border border-[var(--border-secondary)] bg-[var(--bg-inset)] p-4">
                <h3 className="mb-1 text-sm font-medium text-[var(--text-primary)]">{item.title}</h3>
                <p className="text-xs text-[var(--text-muted)]">{item.desc}</p>
                {item.action ? (
                  <button onClick={item.action} className="mt-2 text-xs font-medium text-indigo-400 hover:underline">+ Add Lead</button>
                ) : (
                  <span className={`mt-2 inline-flex rounded-full bg-${item.color}-500/10 px-2 py-0.5 text-xs text-${item.color}-400`}>{item.badge}</span>
                )}
              </div>
            ))}
            {/* CSV Bulk Import */}
            <div className="rounded-lg border border-dashed border-[var(--border-secondary)] bg-[var(--bg-inset)] p-4">
              <h3 className="mb-1 text-sm font-medium text-[var(--text-primary)]">CSV / Excel Import</h3>
              <p className="text-xs text-[var(--text-muted)]">Bulk import leads from a .csv or .xlsx file. You check the columns before anything is saved.</p>
              <label className="mt-2 flex cursor-pointer items-center gap-1 text-xs font-medium text-indigo-400 hover:underline">
                <Download size={12} />
                {importingCsv ? 'Reading file...' : 'Upload CSV / Excel'}
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  disabled={importingCsv}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleLeadFileChosen(file);
                    e.target.value = '';
                  }}
                />
              </label>
              <button
                type="button"
                onClick={downloadSampleCsv}
                className="mt-1 flex items-center gap-1 text-xs font-medium text-indigo-400 hover:underline"
              >
                <Download size={12} />
                Download sample
              </button>
              <p className="mt-1 text-[10px] text-[var(--text-muted)]">
                Column names can be your own (Name, Mobile / Contact, City / Address, Firm, Product, Qty, Remarks, Source).
                A <strong>phone</strong> is required — rows without one are skipped. No source column → saved as Manual.
              </p>
            </div>
          </div>
          {fileImport && (
            <LeadFileImportPreview
              key={fileImport.fileName + fileImport.table.length}
              fileName={fileImport.fileName}
              table={fileImport.table}
              initialColumns={fileImport.columns}
              onClose={() => setFileImport(null)}
              onImported={() => { fetchLeads(); fetchDashStats(); }}
            />
          )}
        </div>
      )}

      {/* Sources Panel */}
      {showSources && (
        <div className="mb-6 rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">Lead Sources</h2>
            <div className="flex items-center gap-2">
              {canManageSources && <button onClick={() => setShowAddSource(!showAddSource)} className="flex items-center gap-1 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:from-indigo-700 hover:to-purple-700">
                <Plus size={14} /> Add Source
              </button>}
              <button onClick={() => setShowSources(false)} className="rounded-lg p-1 text-[var(--text-muted)] hover:bg-[var(--bg-inset)]"><X size={18} /></button>
            </div>
          </div>
          {canManageSources && showAddSource && (
            <div className="mb-4 rounded-lg border border-[var(--border-secondary)] bg-[var(--bg-inset)] p-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <InputField label="Source Name" value={sourceForm.name} onChange={(v) => setSourceForm((f) => ({ ...f, name: v }))} placeholder="e.g. IndiaMart API" />
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Type</label>
                  <select value={sourceForm.type} onChange={(e) => setSourceForm((f) => ({ ...f, type: e.target.value }))} className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none">
                    {SOURCE_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                <InputField label="Config (JSON)" value={sourceForm.config} onChange={(v) => setSourceForm((f) => ({ ...f, config: v }))} placeholder='{"apiKey":"..."}' />
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button onClick={() => setShowAddSource(false)} className="rounded-lg border border-[var(--border-primary)] px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]">Cancel</button>
                <button onClick={handleAddSource} disabled={savingSource || !sourceForm.name.trim()} className="flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                  {savingSource ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Save
                </button>
              </div>
            </div>
          )}
          {sourcesLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-indigo-500" /><span className="ml-2 text-sm text-[var(--text-muted)]">Loading...</span></div>
          ) : sources.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-[var(--text-muted)]"><Database size={32} className="mb-2 opacity-50" /><p className="text-sm">No sources configured</p></div>
          ) : (
            <div className="divide-y divide-[var(--border-secondary)]">
              {sources.map((source) => (
                <div key={source.id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    {canManageSources ? (
                      <button onClick={() => toggleSourceActive(source)} aria-label={`${source.isActive ? 'Switch off' : 'Switch on'} ${source.name}`}>
                        {source.isActive ? <ToggleRight size={24} className="text-green-400" /> : <ToggleLeft size={24} className="text-[var(--text-muted)]" />}
                      </button>
                    ) : (
                      <span aria-label={source.isActive ? 'On' : 'Off'} title={source.isActive ? 'On' : 'Off'}>
                        {source.isActive ? <ToggleRight size={24} className="text-green-400 opacity-60" /> : <ToggleLeft size={24} className="text-[var(--text-muted)] opacity-60" />}
                      </span>
                    )}
                    <div>
                      <p className="text-sm font-medium text-[var(--text-primary)]">{source.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${SOURCE_BADGE[source.type]?.bg ?? 'bg-gray-500/10'} ${SOURCE_BADGE[source.type]?.text ?? 'text-gray-400'}`}>{source.type}</span>
                        {source.importCount != null && <span className="text-xs text-[var(--text-muted)]">{source.importCount} leads</span>}
                      </div>
                    </div>
                  </div>
                  {canManageSources && <button onClick={() => deleteSource(source)} aria-label={`Delete source ${source.name}`} className="rounded-lg p-1.5 text-[var(--text-muted)] hover:bg-red-500/10 hover:text-red-400"><Trash2 size={16} /></button>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stage Tabs */}
      <div data-tour="leads-stage-tabs" className="mb-4 flex items-center gap-1 overflow-x-auto rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] p-1">
        <TabButton active={activeTab === 'all'} onClick={() => handleStageTabChange('all')} label="All" count={bookTotal} />
        {LEAD_STAGES.map((s) => (
          <TabButton key={s.value} active={activeTab === s.value} onClick={() => handleStageTabChange(s.value)} label={s.label} count={stageCounts[s.value]} />
        ))}
      </div>

      {/* Search & Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            type="text"
            data-tour="leads-search"
            placeholder="Search by name, email, company, phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] py-2 pl-9 pr-4 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <button
          data-tour="leads-filters"
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${showFilters ? 'border-indigo-500/50 bg-indigo-500/10 text-indigo-400' : 'border-[var(--border-primary)] bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]'}`}
        >
          <Filter size={16} /> Filters
        </button>
        {filterOwner && ownerBreakdown.canFilterByOwner && (
          <button
            type="button"
            aria-label={`Remove owner filter: ${selectedOwnerLabel}`}
            onClick={() => handleOwnerFilterChange('')}
            className="flex items-center gap-1 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-3 py-1.5 text-xs font-medium text-indigo-300"
          >
            {selectedOwnerLabel}
            <X size={13} />
          </button>
        )}
        {selected.size > 0 && (
          <button onClick={handleBulkProcess} disabled={bulkProcessing} className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-2 text-sm font-medium text-white hover:from-green-700 hover:to-emerald-700 disabled:opacity-50">
            {bulkProcessing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            Process Selected ({selected.size})
          </button>
        )}
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap gap-3 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4">
          <LeadOwnerFilter
            breakdown={ownerBreakdown}
            value={filterOwner}
            onChange={handleOwnerFilterChange}
          />
          <select value={filterPriority} onChange={(e) => { setFilterPriority(e.target.value); setPage(1); }} className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none">
            <option value="">All Priorities</option>
            {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          {activeTab === 'lost' && (
            <label className="flex items-center gap-2 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-secondary)]">
              <span>Lost Reason</span>
              <select
                aria-label="Lost Reason"
                value={filterLostReason}
                onChange={(e) => { setFilterLostReason(e.target.value); setPage(1); setSelected(new Set()); }}
                className="bg-transparent text-[var(--text-primary)] focus:outline-none"
              >
                <option value="">All Lost Reasons</option>
                {LOST_REASON_FILTER_OPTIONS.map((reason) => (
                  <option key={reason.value} value={reason.value}>{reason.label}</option>
                ))}
              </select>
            </label>
          )}
          {/* T6 — multi-select 7-key source chip (replaces the old single
              raw-sourceName dropdown; sent as a CSV `source` param). */}
          <div className="flex flex-wrap items-center gap-1 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-2 py-1.5">
            <span className="px-1 text-xs text-[var(--text-muted)]">Source:</span>
            {LEAD_SOURCE_KEYS.map((key) => {
              const active = filterSourceKeys.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setFilterSourceKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
                    setPage(1);
                  }}
                  className={`rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${active ? 'bg-indigo-500/20 text-indigo-300' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]'}`}
                >
                  {LEAD_SOURCE_KEY_LABELS[key]}
                </button>
              );
            })}
          </div>
          {/* T6 — date-range chip (dateType + IST calendar from/to). */}
          <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-2 py-1.5">
            <select value={filterDateType} onChange={(e) => { setFilterDateType(e.target.value as typeof filterDateType); setPage(1); }} className="rounded-lg border border-[var(--border-primary)] bg-transparent px-1 py-1 text-xs text-[var(--text-primary)] focus:outline-none">
              {DATE_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <input type="date" value={filterDateFrom} onChange={(e) => { setFilterDateFrom(e.target.value); setPage(1); }} className="rounded-lg border border-[var(--border-primary)] bg-transparent px-1 py-1 text-xs text-[var(--text-primary)] focus:outline-none" />
            <span className="text-xs text-[var(--text-muted)]">to</span>
            <input type="date" value={filterDateTo} onChange={(e) => { setFilterDateTo(e.target.value); setPage(1); }} className="rounded-lg border border-[var(--border-primary)] bg-transparent px-1 py-1 text-xs text-[var(--text-primary)] focus:outline-none" />
          </div>
          <select value={filterCustomerType} onChange={(e) => { setFilterCustomerType(e.target.value); setPage(1); }} className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none">
            <option value="">All Types</option>
            <option value="b2b">B2B</option>
            <option value="b2c">B2C</option>
          </select>
          {(filterOwner || filterLostReason || filterPriority || filterSourceKeys.length > 0 || filterCustomerType || filterDateFrom || filterDateTo) && (
            <button data-tour="leads-clear" onClick={() => { setFilterOwner(''); setFilterOwnerName(''); setFilterLostReason(''); setFilterPriority(''); setFilterSourceKeys([]); setFilterCustomerType(''); setFilterDateFrom(''); setFilterDateTo(''); setPage(1); setSelected(new Set()); }} className="flex items-center gap-1 rounded-lg border border-[var(--border-primary)] px-3 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]">
              <X size={14} /> Clear
            </button>
          )}
        </div>
      )}

      {createdNotice && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-300">
          <span>{createdNotice}</span>
          <span className="flex items-center gap-2">
            <button
              // 🔒 The warning is NOT cleared here. This button does not reach
              // the sidebar `scope`, and clearing the banner on faith told the
              // rep the lead was visible before any list had said so. Instead
              // the lead is put back in the pending slot: the next response
              // decides, exactly as it did after the create. `filterDateType`
              // is reset too — it silently narrows every date filter.
              onClick={() => { setFilterOwner(''); setFilterOwnerName(''); setFilterLostReason(''); setFilterPriority(''); setFilterSourceKeys([]); setFilterCustomerType(''); setFilterDateFrom(''); setFilterDateTo(''); setFilterDateType('created'); setPage(1); setSelected(new Set()); if (lastCreatedRef.current) pendingCreatedRef.current = { ...lastCreatedRef.current, clearedNote: null }; }}
              className="rounded-lg border border-amber-500/40 px-3 py-1.5 text-xs font-medium hover:bg-amber-500/20"
            >Clear filters</button>
            <button onClick={() => setCreatedNotice(null)} aria-label="Dismiss" className="text-amber-300/70 hover:text-amber-200"><X size={14} /></button>
          </span>
        </div>
      )}

      {/* Main Content: Board or List */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-indigo-500" /><span className="ml-2 text-sm text-[var(--text-muted)]">Loading leads...</span></div>
      ) : error ? (
        <div className="flex flex-col items-center py-20"><p className="text-sm text-red-400">{error}</p><button onClick={fetchLeads} className="mt-2 text-sm text-indigo-400 hover:underline">Retry</button></div>
      ) : viewMode === 'kanban' ? (
        <KanbanBoard
          leads={leads}
          pipelineData={pipelineData}
          onOpenDetail={openDetail}
          onCardClick={(lead) => setDispositionLead(lead)}
          maxMissedFollowUps={maxMissedFollowUps}
        />
      ) : leads.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] py-20 text-[var(--text-muted)]">
          <TrendingUp size={40} className="mb-2 opacity-50" />
          <p className="text-sm">No leads found</p>
          <p className="text-xs mt-1">Add leads manually or configure a source to start importing</p>
          <button onClick={() => setShowAddLead(true)} className="mt-3 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors" style={{ background: 'var(--ink-0)', color: 'var(--ivory-0)' }}><Plus size={14} /> Add Lead</button>
        </div>
      ) : (
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-secondary)] bg-[var(--bg-inset)] text-left text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
                  <th className="px-3 py-3 w-10">
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="rounded accent-indigo-500" title="Select all new leads" />
                  </th>
                  <th className="px-3 py-3">Lead</th>
                  <th className="px-3 py-3">Contact</th>
                  <th className="px-3 py-3">Product</th>
                  <th className="px-3 py-3">Stage</th>
                  {/* Pipeline-Stage-Overhaul (May 2 2026): "Interest"
                      column (HOT/WARM/COLD heat tag) removed — Stage is the
                      single source of truth for lead state. */}
                  <th className="px-3 py-3" title="Auto-calculated from lead data">Score</th>
                  <th className="px-3 py-3">Assigned</th>
                  <th className="px-3 py-3">Follow-up</th>
                  <th className="px-3 py-3">Source</th>
                  <th className="px-3 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-secondary)]">
                {leads.map((lead) => {
                  const isNew = lead.status === 'new';
                  const isProcessing = processing.has(lead.id);
                  const isFollowUpOverdue = lead.followUpDate && new Date(lead.followUpDate) < new Date() && !['won', 'dead', 'lost'].includes(lead.leadStage);
                  // Pipeline-Stage-Overhaul (May 2 2026): interestInfo lookup
                  // removed — the Interest column was deleted from the table.
                  return (
                    <tr key={lead.id} className="hover:bg-[var(--bg-inset)] transition-colors cursor-pointer" onClick={() => openDetail(lead)}>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        {isNew && <input type="checkbox" checked={selected.has(lead.id)} onChange={() => toggleSelect(lead.id)} className="rounded accent-indigo-500" />}
                      </td>
                      <td className="px-3 py-3">
                        <div>
                          <p className="font-medium text-[var(--text-primary)] hover:text-indigo-400 hover:underline transition-colors">{lead.name || 'Unknown'}</p>
                          {lead.company && <p className="text-xs text-[var(--text-muted)]">{lead.company}</p>}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-0.5">
                          {lead.email && <span className="flex items-center gap-1 text-xs text-[var(--text-secondary)]"><Mail size={10} />{lead.email}</span>}
                          {lead.phone && (
                            <span className="flex items-center gap-1 text-xs text-[var(--text-secondary)]">
                              <Phone size={10} />{lead.phone}
                              {lead.phoneStatus && phoneStatusBadge(lead.phoneStatus)}
                            </span>
                          )}
                          {lead.callAttempts != null && lead.callAttempts > 0 && (
                            <span className="flex items-center gap-1 text-[10px] text-[var(--text-muted)]"><PhoneCall size={9} />{lead.callAttempts} call{lead.callAttempts > 1 ? 's' : ''}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-[var(--text-secondary)] text-xs max-w-[120px] truncate">{lead.product || '-'}</td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-col items-start gap-1.5">
                          {/* Fix round 1 (2026-09-25): the `<select>` → handleStageChange →
                              PATCH .../stage 403s `manual_stage_change_disabled` for EVERY
                              role, admins included (lead-integrations.routes.ts ~3033 gates
                              on `_source`, which this call never sent) — task-5-review.md.
                              There is no admin override; show the static stage badge always. */}
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_BADGE[lead.leadStage]?.bg ?? ''} ${STAGE_BADGE[lead.leadStage]?.text ?? ''}`}>
                            {LEAD_STAGES.find((s) => s.value === lead.leadStage)?.label ?? lead.leadStage}
                          </span>
                          {resolveCanonical(lead) === 'lost' && (
                            <LostReasonBadge
                              reason={lead.exactLostReason ?? null}
                              competitorName={lead.exactLostCompetitorName}
                              freeText={lead.exactLostFreeText}
                            />
                          )}
                        </div>
                      </td>
                      {/* Pipeline-Stage-Overhaul (May 2 2026): Interest cell
                          removed — heat-tag (HOT/WARM/COLD) duplicated the
                          Stage chip and contradicted it on cards like Manak's.
                          Stage column above is the single source of truth. */}
                      <td className="px-3 py-3">{scoreBadge(lead.score)}</td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          // QA-FIELDLEAD FIX 1: prefer the canonical User FK. Field/
                          // manual leads carry ONLY assignedToId (a User id) — the
                          // legacy `assignedTo` String is empty on them, so binding to
                          // it mislabelled ~414 SS leads as "Unassigned". The dropdown
                          // options are Employee ids, which never equal a User id, so we
                          // inject a synthetic option (id + assignedToName) to pre-select
                          // and display the real owner. Reassign still targets a real
                          // Employee option via onChange.
                          const ownerId = lead.assignedToId || lead.assignedTo || '';
                          // QAF-R3: match the owner against BOTH the Employee.id
                          // (legacy `assignedTo`) AND the Employee's linked userId
                          // (canonical `assignedToId`). The <select> value must be
                          // an Employee.id (option value), so resolve it here.
                          const ownerEmp = ownerId
                            ? employees.find((em) => em.id === ownerId || em.userId === ownerId)
                            : undefined;
                          const selectValue = ownerEmp?.id || ownerId;
                          const ownerMatched = !!ownerEmp;
                          return (
                            <select
                              value={selectValue}
                              onChange={(e) => {
                                const emp = employees.find((em) => em.id === e.target.value);
                                handleAssign(lead.id, e.target.value, emp ? `${emp.firstName} ${emp.lastName}` : '');
                              }}
                              className="w-24 truncate rounded-lg border border-[var(--border-primary)] bg-transparent px-1 py-0.5 text-xs text-[var(--text-secondary)] focus:border-indigo-500 focus:outline-none"
                            >
                              <option value="">Unassigned</option>
                              {ownerId && !ownerMatched && (
                                <option value={ownerId}>{lead.assignedToName || 'Assigned'}</option>
                              )}
                              {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>)}
                            </select>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-3">
                        {lead.followUpDate ? (
                          <span className={`flex items-center gap-1 text-xs ${isFollowUpOverdue ? 'text-red-400 font-medium' : 'text-[var(--text-secondary)]'}`}>
                            {isFollowUpOverdue && <AlertCircle size={10} />}
                            {formatDate(lead.followUpDate)}
                          </span>
                        ) : <span className="text-xs text-[var(--text-muted)]">-</span>}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${SOURCE_BADGE[lead.sourceName?.toLowerCase()]?.bg ?? 'bg-gray-500/10'} ${SOURCE_BADGE[lead.sourceName?.toLowerCase()]?.text ?? 'text-gray-400'}`}>
                          {lead.sourceName?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {isNew && (
                            <>
                              <button onClick={() => handleProcess(lead.id)} disabled={isProcessing} className="flex items-center gap-1 rounded-lg bg-green-500/10 px-2 py-1 text-xs font-medium text-green-400 hover:bg-green-500/20 disabled:opacity-50" title="Convert to Contact">
                                {isProcessing ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Convert
                              </button>
                              <button onClick={() => handleReject(lead.id)} disabled={isProcessing} className="rounded-lg bg-red-500/10 p-1 text-red-400 hover:bg-red-500/20 disabled:opacity-50" title="Reject">
                                <XCircle size={14} />
                              </button>
                            </>
                          )}
                          {lead.contactId && (
                            <Link to={`/contacts/${lead.contactId}`} className="rounded-lg bg-indigo-500/10 p-1 text-indigo-400 hover:bg-indigo-500/20" title="View Contact">
                              <ExternalLink size={14} />
                            </Link>
                          )}
                          <button onClick={() => openDetail(lead)} className="rounded-lg p-1 text-[var(--text-muted)] hover:bg-[var(--bg-inset)] hover:text-[var(--text-primary)]" title="View Details">
                            <Eye size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && viewMode === 'list' && leads.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-[var(--text-muted)]">
            {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, meta.total)} of {meta.total}
          </p>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="flex items-center gap-1 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] disabled:opacity-50">
              <ChevronLeft size={14} /> Prev
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pn: number;
              if (totalPages <= 5) pn = i + 1;
              else if (page <= 3) pn = i + 1;
              else if (page >= totalPages - 2) pn = totalPages - 4 + i;
              else pn = page - 2 + i;
              return (
                <button key={pn} onClick={() => setPage(pn)} className={`min-w-[32px] rounded-lg px-2 py-1.5 text-sm font-medium ${pn === page ? 'bg-indigo-600 text-white' : 'border border-[var(--border-primary)] bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]'}`}>
                  {pn}
                </button>
              );
            })}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="flex items-center gap-1 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] disabled:opacity-50">
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {markWonLead && (
        <MarkWonDialog
          leadId={markWonLead.id}
          leadName={markWonLead.name}
          role={authUser?.role?.name}
          onClose={() => setMarkWonLead(null)}
          onWon={() => {
            const wonId = markWonLead.id;
            setMarkWonLead(null);
            fetchLeads(); fetchDashStats();
            if (selectedLead?.id === wonId) setSelectedLead(null);
          }}
        />
      )}

      {/* Lead Detail Drawer */}
      {selectedLead && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setSelectedLead(null)}>
          <div className="w-full max-w-lg overflow-y-auto border-l border-[var(--border-primary)] bg-[var(--bg-primary)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Drawer Header */}
            <div className="sticky top-0 z-10 border-b border-[var(--border-primary)] bg-[var(--bg-primary)] px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate text-xl font-bold text-[var(--text-primary)]">{selectedLead.name || 'Unknown Lead'}</h2>
                    {/* WEB-EDIT: admin-only — correct a wrongly-entered number/name */}
                    {canEditContact && !showEditContact && (
                      <button
                        type="button"
                        onClick={openEditContact}
                        className="flex shrink-0 items-center gap-1 rounded-md border border-[var(--border-primary)] px-2 py-0.5 text-[11px] font-medium text-[var(--text-muted)] hover:border-indigo-500/50 hover:text-indigo-300 transition-colors"
                        title="Edit lead name & number (admin)"
                      >
                        <Edit3 size={12} /> Edit number
                      </button>
                    )}
                  </div>
                  {selectedLead.company && <p className="flex items-center gap-1 text-sm text-[var(--text-muted)]"><Building2 size={14} />{selectedLead.company}</p>}
                  {selectedLead.phone && !showEditContact && (
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-[var(--text-muted)]"><Phone size={12} />{formatPhone(selectedLead.phone)}</p>
                  )}
                </div>
                <button onClick={() => setSelectedLead(null)} className="shrink-0 rounded-lg p-1 text-[var(--text-muted)] hover:bg-[var(--bg-inset)]"><X size={20} /></button>
              </div>

              {/* WEB-EDIT (2026-07-07): admin-only inline edit for the locked
                  primary contact (name + phone). Non-admins never render this. */}
              {canEditContact && showEditContact && (
                <div className="mt-3 rounded-lg border border-[var(--color-primary,#E07A16)]/40 bg-[var(--color-primary,#E07A16)]/5 p-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-primary,#E07A16)]">Edit contact (admin)</p>
                  <div className="space-y-2">
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-[var(--text-muted)]">Name</label>
                      <input
                        type="text"
                        value={editContactForm.name}
                        onChange={(e) => setEditContactForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="Lead name"
                        className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-[var(--text-muted)]">Phone number</label>
                      <input
                        type="tel"
                        inputMode="tel"
                        value={editContactForm.phone}
                        onChange={(e) => setEditContactForm((f) => ({ ...f, phone: e.target.value }))}
                        placeholder="+91 98765 43210"
                        className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                    {editContactError && (
                      <p className="flex items-center gap-1 text-[11px] text-red-400"><AlertCircle size={12} />{editContactError}</p>
                    )}
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        onClick={handleEditContact}
                        disabled={savingEditContact}
                        className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                      >
                        {savingEditContact ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Save
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowEditContact(false); setEditContactError(null); }}
                        disabled={savingEditContact}
                        className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm font-medium text-[var(--text-muted)] hover:bg-[var(--bg-inset)] disabled:opacity-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {/* Stage pill */}
              <div className="mt-2 flex flex-wrap gap-2">
                {stageBadge(selectedLead.leadStage)}
                {resolveCanonical(selectedLead) === 'lost' && (
                  <LostReasonBadge
                    reason={selectedLead.exactLostReason ?? null}
                    competitorName={selectedLead.exactLostCompetitorName}
                    freeText={selectedLead.exactLostFreeText}
                  />
                )}
                {selectedLead.interestLevel && (
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${INTEREST_LEVELS.find((il) => il.value === selectedLead.interestLevel)?.bg} ${INTEREST_LEVELS.find((il) => il.value === selectedLead.interestLevel)?.text}`}>
                    {selectedLead.interestLevel?.charAt(0).toUpperCase()}{selectedLead.interestLevel?.slice(1)} Interest
                  </span>
                )}
                {selectedLead.isDuplicate && (
                  <span className="inline-flex rounded-full bg-yellow-500/10 px-2.5 py-0.5 text-xs text-yellow-400">Duplicate</span>
                )}
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Quick Actions Bar — prominent stage change + call */}
              <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-300">Quick Actions</p>
                  <span className="rounded bg-indigo-500/20 px-1.5 py-0.5 text-[9px] font-mono text-indigo-200">{BUILD_TAG}</span>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  {/* Fix round 1 (2026-09-25): no admin override — see the row-level
                      comment above. The select 403s for every role. */}
                  <span className="flex-1 rounded-lg border border-indigo-500/40 bg-[var(--bg-surface)] px-3 py-2 text-sm font-medium text-[var(--text-primary)]">
                    {LEAD_STAGES.find((s) => s.value === selectedLead.leadStage)?.label ?? selectedLead.leadStage}
                  </span>
                  {selectedLead.phone && (
                    <button
                      type="button"
                      onClick={() => {
                        setPostCallForm({ outcome: 'answered', stage: selectedLead.leadStage, notes: '' });
                        setShowPostCallPrompt(true);
                        dialPhone(selectedLead.phone);
                      }}
                      className="flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
                      title={`Dial ${telHref(selectedLead.phone)}`}
                    >
                      <PhoneCall size={14} /> Call Now
                    </button>
                  )}
                  {selectedLead.phone && (
                    <a
                      href={waHref(selectedLead.phone)}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                      title={`Open WhatsApp: ${waHref(selectedLead.phone)}`}
                    >
                      <Send size={14} /> WhatsApp
                    </a>
                  )}
                  {/* B10 (2026-09-24 LOCKED SPEC) — assign this lead to a dealer
                      from the WEB, not only from the phone. Same picker and the
                      same POST /leads/:id/assign-dealer the phone uses; the
                      server decides which dealers this person may pick (B11). */}
                  <button
                    type="button"
                    onClick={() => setAssignFor({ id: selectedLead.id, name: selectedLead.name || '' })}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-300 hover:bg-amber-500/20"
                    title="Assign this lead to a dealer"
                  >
                    <Building2 size={14} /> Assign to dealer
                  </button>
                </div>
                {selectedLead.phone && (
                  <p className="mt-2 break-all text-[10px] font-mono text-indigo-200/70">
                    Dial: <span className="text-indigo-200">{toE164(selectedLead.phone)}</span>
                    {' · '}
                    WA: <span className="text-emerald-300">wa.me/{toE164(selectedLead.phone).replace(/\D/g, '')}</span>
                  </p>
                )}
              </div>

              {/* Stage & Priority controls */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Pipeline Stage</label>
                  {/* Fix round 1 (2026-09-25): no admin override — see the row-level
                      comment above. The select 403s for every role. */}
                  <p className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)]">
                    {LEAD_STAGES.find((s) => s.value === selectedLead.leadStage)?.label ?? selectedLead.leadStage}
                  </p>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Priority</label>
                  <select
                    value={selectedLead.priority}
                    onChange={(e) => handlePriorityChange(selectedLead.id, e.target.value)}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none"
                  >
                    {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Interest Level */}
              <div>
                <label className="mb-2 block text-xs font-medium text-[var(--text-muted)]">Interest Level</label>
                <div className="flex gap-2">
                  {INTEREST_LEVELS.map((il) => (
                    <button
                      key={il.value}
                      onClick={() => handleInterestLevel(selectedLead.id, il.value as 'hot' | 'warm' | 'cold')}
                      className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${selectedLead.interestLevel === il.value ? `${il.bg} ${il.text} border-current` : 'border-[var(--border-primary)] text-[var(--text-muted)] hover:bg-[var(--bg-inset)]'}`}
                    >
                      {il.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact Info + Phone Status */}
              <div>
                <h3 className="mb-2 text-sm font-semibold text-[var(--text-primary)]">Contact Information</h3>
                <div className="rounded-lg border border-[var(--border-secondary)] bg-[var(--bg-surface)] p-3 space-y-3">
                  {selectedLead.email ? (
                    <div>
                      <p className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                        <Mail size={14} className="text-[var(--text-muted)] shrink-0" />{selectedLead.email}
                      </p>
                      <a
                        href={`mailto:${selectedLead.email}?subject=${encodeURIComponent('Lead Verification')}&body=${encodeURIComponent('Please confirm your WhatsApp/mobile number to continue.')}`}
                        className="mt-1.5 ml-6 flex items-center gap-1 rounded-lg bg-blue-500/10 px-2.5 py-1 text-xs font-medium text-blue-400 hover:bg-blue-500/20 transition-colors"
                      >
                        <Send size={10} /> Send Verification Email
                      </a>
                    </div>
                  ) : (
                    /* Apr 27 (audit P1) — replace alert() with a real prompt
                       that captures the email and PATCHes it onto the lead.
                       No new endpoint; reuses /lead-integrations/lead-imports/:id/details. */
                    <button
                      onClick={async () => {
                        if (!selectedLead) return;
                        const email = window.prompt(
                          `Capture email for ${selectedLead.name ?? 'this lead'} (will be saved to the lead record):`,
                        );
                        if (!email || !email.includes('@')) return;
                        try {
                          await api.patch(
                            `/lead-integrations/lead-imports/${selectedLead.id}/details`,
                            { email },
                          );
                          setSelectedLead((l) => (l ? { ...l, email } : null));
                        } catch (err) {
                          window.alert(`Could not save email: ${(err as Error).message}`);
                        }
                      }}
                      className="flex items-center gap-1 rounded-lg border border-dashed border-[var(--border-primary)] px-2.5 py-1.5 text-xs text-[var(--text-muted)] hover:bg-[var(--bg-inset)] transition-colors"
                    >
                      <Mail size={10} /> Request Email
                    </button>
                  )}
                  {selectedLead.phone && (
                    <div>
                      <p className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                        <Phone size={14} className="text-[var(--text-muted)] shrink-0" />
                        <a
                          href={telHref(selectedLead.phone)}
                          className="text-indigo-400 hover:text-indigo-300 hover:underline"
                        >
                          {formatPhone(selectedLead.phone)}
                        </a>
                        <a
                          href={waHref(selectedLead.phone)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-emerald-400 hover:text-emerald-300"
                          title="Open in WhatsApp"
                        >
                          <Send size={12} />
                        </a>
                      </p>
                      {/* WhatsApp / Phone Status Widget */}
                      <div className="mt-2 ml-6">
                        <p className="mb-1.5 text-xs text-[var(--text-muted)]">WhatsApp / Phone Status</p>
                        <div className="flex gap-2">
                          {([
                            { value: 'valid', label: 'Valid', icon: CheckCircle, active: 'bg-green-500/10 text-green-400 border-green-500/30' },
                            { value: 'not_functional', label: 'Not Active', icon: AlertTriangle, active: 'bg-orange-500/10 text-orange-400 border-orange-500/30' },
                            { value: 'incorrect', label: 'Incorrect', icon: Ban, active: 'bg-red-500/10 text-red-400 border-red-500/30' },
                          ] as const).map((opt) => (
                            <button
                              key={opt.value}
                              onClick={() => handlePhoneStatus(selectedLead.id, opt.value)}
                              disabled={updatingPhone}
                              className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${selectedLead.phoneStatus === opt.value ? opt.active : 'border-[var(--border-primary)] text-[var(--text-muted)] hover:bg-[var(--bg-inset)]'}`}
                            >
                              <opt.icon size={11} />
                              {opt.label}
                            </button>
                          ))}
                          <button
                            onClick={() => handleWhatsAppVerify(selectedLead)}
                            className="flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1.5 text-xs font-medium text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                            title="Verify via WhatsApp (ChatKart)"
                          >
                            <Send size={11} /> WhatsApp Verify
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedLead.location && (
                    <p className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                      <MapPin size={14} className="text-[var(--text-muted)] shrink-0" />{selectedLead.location}
                    </p>
                  )}
                </div>
              </div>

              {/* Call Tracking */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                    Call Tracking
                    {(selectedLead.callAttempts ?? 0) > 0 && (
                      <span className="ml-2 rounded-full bg-indigo-500/10 px-2 py-0.5 text-xs font-medium text-indigo-400">
                        {selectedLead.callAttempts} attempt{selectedLead.callAttempts !== 1 ? 's' : ''}
                      </span>
                    )}
                  </h3>
                  <div className="flex items-center gap-2">
                    {selectedLead.phone && (
                      <a
                        href={telHref(selectedLead.phone)}
                        onClick={() => {
                          setPostCallForm({ outcome: 'answered', stage: selectedLead.leadStage, notes: '' });
                          setShowPostCallPrompt(true);
                        }}
                        className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 transition-colors"
                        title={`Dial ${formatPhone(selectedLead.phone)}`}
                      >
                        <PhoneCall size={12} /> Call Now
                      </a>
                    )}
                    <button
                      onClick={() => setShowCallLog(!showCallLog)}
                      className="flex items-center gap-1.5 rounded-lg bg-indigo-500/10 px-3 py-1.5 text-xs font-medium text-indigo-400 hover:bg-indigo-500/20 transition-colors"
                    >
                      <PhoneCall size={12} /> Log Call
                    </button>
                  </div>
                </div>

                {selectedLead.lastCalledAt && (
                  <div className="mb-2 rounded-lg border border-[var(--border-secondary)] bg-[var(--bg-surface)] px-3 py-2 text-xs text-[var(--text-muted)]">
                    Last call: {formatDate(selectedLead.lastCalledAt)}
                    {selectedLead.callOutcome && <> · <span className="text-[var(--text-secondary)]">{CALL_OUTCOMES.find((o) => o.value === selectedLead.callOutcome)?.label ?? selectedLead.callOutcome}</span></>}
                  </div>
                )}

                {showCallLog && (
                  <div className="rounded-lg border border-indigo-500/20 bg-indigo-500/5 p-3 space-y-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Outcome</label>
                      <select
                        value={callForm.outcome}
                        onChange={(e) => setCallForm((f) => ({ ...f, outcome: e.target.value }))}
                        className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none"
                      >
                        {CALL_OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Notes (optional)</label>
                      <input
                        type="text"
                        value={callForm.notes}
                        onChange={(e) => setCallForm((f) => ({ ...f, notes: e.target.value }))}
                        placeholder="What was discussed..."
                        className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setShowCallLog(false)} className="rounded-lg border border-[var(--border-primary)] px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]">Cancel</button>
                      <button onClick={handleLogCall} disabled={loggingCall} className="flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                        {loggingCall ? <Loader2 size={12} className="animate-spin" /> : <PhoneCall size={12} />}
                        Save Call
                      </button>
                    </div>
                  </div>
                )}
                {(selectedLead.phoneStatus === 'incorrect' || selectedLead.phoneStatus === 'not_functional') && (
                  <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                    <p className="text-xs text-amber-300 mb-2">
                      WhatsApp not reliable. Use direct call fallback, then email fallback if unreachable.
                    </p>
                    <div className="flex gap-2">
                      {selectedLead.phone && (
                        <a
                          href={telHref(selectedLead.phone)}
                          className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                        >
                          <PhoneCall size={12} /> Call Now
                        </a>
                      )}
                      {selectedLead.email && (
                        <a
                          href={`mailto:${selectedLead.email}?subject=${encodeURIComponent('Follow-up on your enquiry')}`}
                          className="flex items-center gap-1.5 rounded-lg border border-[var(--border-primary)] px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]"
                        >
                          <Mail size={12} /> Email Fallback
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Activity History */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">Activity History</h3>
                  <button
                    onClick={() => { setShowActivityLog(!showActivityLog); if (!showActivityLog && activities.length === 0) fetchActivities(selectedLead.id); }}
                    className="flex items-center gap-1.5 rounded-lg bg-purple-500/10 px-3 py-1.5 text-xs font-medium text-purple-400 hover:bg-purple-500/20 transition-colors"
                  >
                    <Plus size={12} /> Log Activity
                  </button>
                </div>

                {showActivityLog && (
                  <div className="rounded-lg border border-purple-500/20 bg-purple-500/5 p-3 space-y-2 mb-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Activity Type</label>
                      <select
                        value={activityForm.type}
                        onChange={(e) => setActivityForm((f) => ({ ...f, type: e.target.value }))}
                        className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-purple-500 focus:outline-none"
                      >
                        {CRM_ACTIVITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Title *</label>
                      <input
                        type="text"
                        value={activityForm.title}
                        onChange={(e) => setActivityForm((f) => ({ ...f, title: e.target.value }))}
                        placeholder="e.g. Had a phone conversation with client"
                        className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-purple-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Description (optional)</label>
                      <textarea
                        value={activityForm.description}
                        onChange={(e) => setActivityForm((f) => ({ ...f, description: e.target.value }))}
                        rows={2}
                        placeholder="Details about the activity..."
                        className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-purple-500 focus:outline-none"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setShowActivityLog(false)} className="rounded-lg border border-[var(--border-primary)] px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]">Cancel</button>
                      <button onClick={handleLogActivity} disabled={loggingActivity || !activityForm.title.trim()} className="flex items-center gap-1 rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-purple-700 disabled:opacity-50">
                        {loggingActivity ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                        Save Activity
                      </button>
                    </div>
                  </div>
                )}

                {/* Activity Timeline */}
                {loadingActivities ? (
                  <div className="flex items-center justify-center py-4"><Loader2 size={16} className="animate-spin text-[var(--text-muted)]" /></div>
                ) : activities.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {activities.map((act: any) => (
                      <div key={act.id} className="flex gap-3 rounded-lg border border-[var(--border-secondary)] bg-[var(--bg-surface)] px-3 py-2">
                        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-500/10">
                          <Clock size={12} className="text-purple-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-[var(--text-primary)]">{act.title}</p>
                          {act.description && <p className="text-[11px] text-[var(--text-muted)] truncate">{act.description}</p>}
                          <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                            <span className="inline-flex rounded-full bg-purple-500/10 px-1.5 py-0.5 text-purple-400 mr-1">{CRM_ACTIVITY_TYPES.find((t) => t.value === act.type)?.label ?? act.type}</span>
                            {formatDate(act.createdAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !showActivityLog ? (
                  <p className="text-xs text-[var(--text-muted)]">No activities logged yet. Click "Log Activity" to start tracking.</p>
                ) : null}
              </div>

              {/* Product / Requirement */}
              {(selectedLead.product || selectedLead.quantity || selectedLead.requirement || selectedLead.message) && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-[var(--text-primary)]">Product & Requirement</h3>
                  <div className="rounded-lg border border-[var(--border-secondary)] bg-[var(--bg-surface)] p-3 space-y-2">
                    {selectedLead.product && (
                      <p className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                        <Package size={14} className="text-[var(--text-muted)] shrink-0" />{selectedLead.product}
                        {selectedLead.quantity && <span className="text-xs text-[var(--text-muted)]">· Qty: {selectedLead.quantity}</span>}
                      </p>
                    )}
                    {(selectedLead.requirement || selectedLead.message) && (
                      <p className="text-sm text-[var(--text-secondary)] whitespace-pre-wrap">{selectedLead.requirement || selectedLead.message}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Field Photos */}
              {(leadImages.length > 0 || loadingImages) && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-[var(--text-primary)] flex items-center gap-1.5">
                    <ImageIcon size={14} className="text-[var(--text-muted)]" />
                    Field Photos
                    {leadImages.length > 0 && <span className="ml-1 rounded-full bg-indigo-500/10 px-2 py-0.5 text-xs font-medium text-indigo-400">{leadImages.length}</span>}
                  </h3>
                  {loadingImages ? (
                    <div className="flex items-center justify-center py-4"><Loader2 size={16} className="animate-spin text-[var(--text-muted)]" /></div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {leadImages.map((img) => (
                        <button
                          key={img.id}
                          onClick={() => setLightboxImg(img.url)}
                          className="group relative aspect-square overflow-hidden rounded-lg border border-[var(--border-secondary)] bg-[var(--bg-surface)] hover:border-indigo-500/50 transition-colors"
                        >
                          <img
                            src={img.url}
                            alt={img.filename}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                            <p className="w-full truncate px-1.5 py-1 text-[10px] text-white">{img.filename}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Assign */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Assigned To</label>
                {(() => {
                  // QA-FIELDLEAD FIX 1 (detail panel) — same FK-first binding +
                  // synthetic-option fallback as the table cell above.
                  const ownerId = selectedLead.assignedToId || selectedLead.assignedTo || '';
                  // QAF-R3: match owner by Employee.id OR employee.userId; the
                  // <select> value must be an Employee.id.
                  const ownerEmp = ownerId
                    ? employees.find((em) => em.id === ownerId || em.userId === ownerId)
                    : undefined;
                  const selectValue = ownerEmp?.id || ownerId;
                  const ownerMatched = !!ownerEmp;
                  return (
                    <select
                      value={selectValue}
                      onChange={(e) => {
                        const emp = employees.find((em) => em.id === e.target.value);
                        handleAssign(selectedLead.id, e.target.value, emp ? `${emp.firstName} ${emp.lastName}` : '');
                      }}
                      className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none"
                    >
                      <option value="">Unassigned</option>
                      {ownerId && !ownerMatched && (
                        <option value={ownerId}>{selectedLead.assignedToName || 'Assigned'}</option>
                      )}
                      {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>)}
                    </select>
                  );
                })()}
              </div>

              {/* Follow-up Date */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Follow-up Date</label>
                <input
                  type="date"
                  value={selectedLead.followUpDate ? new Date(selectedLead.followUpDate).toISOString().split('T')[0] : ''}
                  onChange={(e) => handleSetFollowUp(selectedLead.id, e.target.value)}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none"
                />
              </div>

              {/* AI Score */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-[var(--text-muted)]">AI Lead Score</label>
                  <button
                    onClick={() => handleScoreLead(selectedLead)}
                    disabled={scoringOne === selectedLead.id}
                    className="flex items-center gap-1 rounded-lg bg-purple-600 px-2 py-1 text-xs font-medium text-white hover:bg-purple-700 disabled:opacity-50"
                  >
                    {scoringOne === selectedLead.id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    Score (2 credits)
                  </button>
                </div>
                {selectedLead.score != null ? (
                  <div className="flex items-center gap-3 rounded-lg border border-[var(--border-secondary)] bg-[var(--bg-surface)] p-3">
                    <div className={`text-3xl font-bold ${selectedLead.score >= 70 ? 'text-green-400' : selectedLead.score >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {selectedLead.score}
                    </div>
                    <div className="text-xs text-[var(--text-muted)]">
                      {selectedLead.score >= 70 ? 'Hot Lead — High conversion potential' : selectedLead.score >= 40 ? 'Warm Lead — Needs nurturing' : 'Cold Lead — Low priority'}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-[var(--text-muted)]">Not scored yet. Process the lead first, then score.</p>
                )}
              </div>

              {/* Capture Details (for processed leads with contactId) */}
              {selectedLead.contactId && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-[var(--text-primary)]">Capture Details</h3>
                    <button
                      onClick={() => setShowCaptureDetails(!showCaptureDetails)}
                      className="flex items-center gap-1.5 rounded-lg bg-indigo-500/10 px-3 py-1.5 text-xs font-medium text-indigo-400 hover:bg-indigo-500/20 transition-colors"
                    >
                      <Edit3 size={12} /> {showCaptureDetails ? 'Hide' : 'Capture'}
                    </button>
                  </div>
                  {showCaptureDetails && (
                    <div className="rounded-lg border border-indigo-500/20 bg-indigo-500/5 p-3 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="mb-1 block text-[10px] font-medium text-[var(--text-muted)]">Interest *</label>
                          <ProductPickerField
                            value={captureForm.interest}
                            onChange={(v) => setCaptureForm((f) => ({ ...f, interest: v }))}
                            placeholder="e.g. Solar Water Heater"
                            size="sm"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[10px] font-medium text-[var(--text-muted)]">Product Requirement *</label>
                          <ProductPickerField
                            value={captureForm.productRequirement}
                            onChange={(v) => setCaptureForm((f) => ({ ...f, productRequirement: v }))}
                            placeholder="e.g. 200L ETC"
                            size="sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] font-medium text-[var(--text-muted)]">Location</label>
                        <input type="text" value={captureForm.location} onChange={(e) => setCaptureForm((f) => ({ ...f, location: e.target.value }))} placeholder="City / Area" className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-2 py-1.5 text-xs text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none" />
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] font-medium text-[var(--text-muted)]">Requirement Details *</label>
                        <textarea value={captureForm.requirement} onChange={(e) => setCaptureForm((f) => ({ ...f, requirement: e.target.value }))} rows={2} placeholder="Detailed requirement from first call..." className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-2 py-1.5 text-xs text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none" />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setShowCaptureDetails(false)} className="rounded-lg border border-[var(--border-primary)] px-2 py-1 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]">Cancel</button>
                        <button onClick={handleCaptureDetails} disabled={savingCapture || !captureForm.interest || !captureForm.productRequirement || !captureForm.requirement} className="flex items-center gap-1 rounded-lg bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                          {savingCapture ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />} Save Details
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Notes</label>
                <textarea
                  value={detailNotes}
                  onChange={(e) => setDetailNotes(e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-indigo-500 focus:outline-none"
                  placeholder="Add notes about this lead..."
                />
                <button
                  onClick={handleSaveNotes}
                  disabled={detailSaving || detailNotes === (selectedLead.notes || '')}
                  className="mt-2 flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {detailSaving ? <Loader2 size={12} className="animate-spin" /> : <Edit3 size={12} />}
                  Save Notes
                </button>
              </div>

              {/* Interaction History */}
              {selectedLead.contactId && (
                <div className="border-t border-[var(--border-primary)] pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-[var(--text-primary)]">Interaction History</h3>
                    <button
                      onClick={() => {
                        if (!selectedLead.contactId) return;
                        setLoadingInteractions(true);
                        api.get(`/sales-teams/interactions/${selectedLead.contactId}`)
                          .then(r => setInteractions(r.data.data || []))
                          .catch(() => {})
                          .finally(() => setLoadingInteractions(false));
                      }}
                      className="text-xs text-indigo-400 hover:text-indigo-300"
                    >
                      <RefreshCw size={12} />
                    </button>
                  </div>
                  {loadingInteractions ? (
                    <div className="flex justify-center py-2"><Loader2 size={14} className="animate-spin text-gray-400" /></div>
                  ) : interactions.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {interactions.map((ix: any) => (
                        <div key={ix.id} className="rounded-lg border border-[var(--border-secondary)] bg-[var(--bg-inset)] p-2 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-[var(--text-primary)]">
                              {ix.agent?.firstName} {ix.agent?.lastName}
                            </span>
                            <span className="text-[var(--text-muted)]">{formatDate(ix.createdAt)}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="rounded-full bg-indigo-500/10 px-1.5 py-0.5 text-[10px] text-indigo-400 capitalize">{ix.type}</span>
                            {ix.stageName && <span className="text-[var(--text-muted)]">@ {ix.stageName}</span>}
                            {ix.duration && <span className="text-[var(--text-muted)]">{Math.floor(ix.duration / 60)}m {ix.duration % 60}s</span>}
                            {ix.outcome && <span className="rounded-full bg-gray-500/10 px-1.5 py-0.5 text-[10px] text-gray-400 capitalize">{ix.outcome}</span>}
                          </div>
                          {ix.summary && <p className="mt-1 text-[var(--text-secondary)]">{ix.summary}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-[var(--text-muted)]">No interactions logged yet.</p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="space-y-3 border-t border-[var(--border-primary)] pt-4">
                <h3 className="text-sm font-semibold text-[var(--text-primary)]">Actions</h3>
                {(teleCrmConfirmedIds.has(selectedLead.id) || (selectedLead.notes || '').includes('[TeleCRM]') || (selectedLead.notes || '').includes('[AURA Sales]')) && (
                  <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-2">
                    <p className="text-xs font-medium text-green-300">AURA Sales App handoff confirmed</p>
                    <p className="text-[11px] text-green-200/80">Lead has been pushed for tele-calling follow-up.</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {selectedLead.status === 'new' && (
                    <button onClick={() => { handleProcess(selectedLead.id); setSelectedLead(null); }} className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700">
                      <CheckCircle2 size={14} /> Convert to Contact
                    </button>
                  )}
                  {selectedLead.contactId && (
                    <Link to={`/contacts/${selectedLead.contactId}`} className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700">
                      <ExternalLink size={14} /> View Contact
                    </Link>
                  )}
                  {selectedLead.dealId && (
                    <Link to={`/deals/${selectedLead.dealId}`} className="flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700">
                      <ArrowUpRight size={14} /> View Deal
                    </Link>
                  )}
                  {/* AURA Sales App Push */}
                  <button
                    onClick={() => handlePushTeleCrm(selectedLead.id)}
                    disabled={pushingTeleCrm}
                    className="flex items-center gap-1.5 rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-sm font-medium text-blue-400 hover:bg-blue-500/20 disabled:opacity-50 transition-colors"
                  >
                    {pushingTeleCrm ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    Push to AURA Sales App
                  </button>
                </div>

                {/* Terminal actions (Won / Dead / Lost) */}
                {!['dead', 'lost', 'won'].includes(selectedLead.leadStage) && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button
                      onClick={() => handleMarkWon(selectedLead.id)}
                      className="flex items-center gap-1.5 rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-1.5 text-xs font-medium text-green-400 hover:bg-green-500/20 transition-colors"
                      title="Deal won — order received"
                    >
                      <CheckCircle size={12} /> Mark Won
                    </button>
                    <button
                      onClick={() => handleMarkDead(selectedLead.id)}
                      className="flex items-center gap-1.5 rounded-lg border border-gray-500/30 bg-gray-500/10 px-3 py-1.5 text-xs font-medium text-gray-400 hover:bg-gray-500/20 transition-colors"
                      title="Can't reach after multiple attempts"
                    >
                      <PhoneOff size={12} /> Mark Dead
                    </button>
                    <button
                      onClick={() => handleMarkLost(selectedLead.id)}
                      className="flex items-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-400 hover:bg-red-500/20 transition-colors"
                      title="Not interested"
                    >
                      <XCircle size={12} /> Mark Lost
                    </button>
                  </div>
                )}

                {/* D4 (2026-09-25 fix round, Task 5) — Reopen on a Lost lead.
                    Revokes the Lost −100 milestone via the same engine call
                    `revokeLostMilestone` uses for D3's "sale on a Lost lead". */}
                {resolveCanonical(selectedLead) === 'lost' && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <LeadReopenButton
                      leadId={selectedLead.id}
                      onReopened={(result) => {
                        setSelectedLead((l) => l ? {
                          ...l,
                          leadStage: result.toStage,
                          canonicalStage: result.toStage,
                          score: result.progressScore ?? l.score,
                        } : null);
                        setLeads((prev) => prev.map((l) => l.id === selectedLead.id
                          ? { ...l, leadStage: result.toStage, canonicalStage: result.toStage, score: result.progressScore ?? l.score }
                          : l));
                        fetchLeads();
                        fetchDashStats();
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Meta info */}
              <div className="border-t border-[var(--border-primary)] pt-4 space-y-1 text-xs text-[var(--text-muted)]">
                <p>Source: <span className={`inline-flex rounded-full px-2 py-0.5 ${SOURCE_BADGE[selectedLead.sourceName?.toLowerCase()]?.bg ?? 'bg-gray-500/10'} ${SOURCE_BADGE[selectedLead.sourceName?.toLowerCase()]?.text ?? 'text-gray-400'}`}>{selectedLead.sourceName?.replace('_', ' ')}</span></p>
                {selectedLead.externalId && <p>External ID: #{selectedLead.externalId}</p>}
                <p>Created: {formatDate(selectedLead.createdAt)}</p>
                {selectedLead.updatedAt && <p>Updated: {formatDate(selectedLead.updatedAt)}</p>}
                <p>Status: <span className="capitalize">{selectedLead.status}</span></p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4" onClick={() => setLightboxImg(null)}>
          <div className="relative max-h-[90vh] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setLightboxImg(null)} className="absolute -right-3 -top-3 z-10 rounded-full bg-[var(--bg-surface)] p-1.5 shadow-lg hover:bg-[var(--bg-inset)]"><X size={16} /></button>
            <img src={lightboxImg} alt="Lead photo" className="max-h-[85vh] max-w-[85vw] rounded-lg object-contain shadow-2xl" />
          </div>
        </div>
      )}

      {/* Post-call prompt modal — opens after "Call Now" is tapped */}
      {showPostCallPrompt && selectedLead && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={() => setShowPostCallPrompt(false)}>
          <div className="w-full max-w-md rounded-2xl border border-indigo-500/30 bg-[var(--bg-surface)] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-[var(--text-primary)]">Call Outcome</h3>
                <p className="text-xs text-[var(--text-muted)]">{selectedLead.name} · {formatPhone(selectedLead.phone || '')}</p>
              </div>
              <button onClick={() => setShowPostCallPrompt(false)} className="rounded-lg p-1 text-[var(--text-muted)] hover:bg-[var(--bg-inset)]">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Outcome */}
              <div>
                <label className="mb-2 block text-xs font-medium text-[var(--text-muted)]">Call Outcome</label>
                <div className="grid grid-cols-2 gap-2">
                  {CALL_OUTCOMES.map((o) => (
                    <button
                      key={o.value}
                      onClick={() => setPostCallForm((f) => ({ ...f, outcome: o.value }))}
                      className={`rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${postCallForm.outcome === o.value ? 'border-indigo-500 bg-indigo-500/10 text-indigo-300' : 'border-[var(--border-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]'}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stage */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Update Pipeline Stage</label>
                <select
                  value={postCallForm.stage || selectedLead.leadStage}
                  onChange={(e) => setPostCallForm((f) => ({ ...f, stage: e.target.value }))}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm font-medium text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none"
                >
                  {LEAD_STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Notes</label>
                <textarea
                  value={postCallForm.notes}
                  onChange={(e) => setPostCallForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="What was discussed? Next steps?"
                  rows={3}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-indigo-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setShowPostCallPrompt(false)}
                className="flex-1 rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePostCall}
                disabled={savingPostCall}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {savingPostCall ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                Save & Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sprint Apr-30: Disposition picker modal — opens when Kanban card is clicked */}
      {dispositionLead && (
        <DispositionPickerModal
          leadId={dispositionLead.id}
          leadName={dispositionLead.name || dispositionLead.company}
          canonicalStage={resolveCanonical(dispositionLead)}
          onClose={() => setDispositionLead(null)}
          onSuccess={() => { setDispositionLead(null); fetchLeads(); fetchDashStats(); }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Kanban Board                                                        */
/* ------------------------------------------------------------------ */

// ─── Sprint Apr-30: canonical Kanban — no drag, disposition picker on click ──

function KanbanBoard({
  leads,
  pipelineData,
  onOpenDetail,
  onCardClick,
  maxMissedFollowUps,
}: {
  leads: Lead[];
  pipelineData?: any;
  onOpenDetail: (lead: Lead) => void;
  onCardClick: (lead: Lead) => void;
  maxMissedFollowUps: number;
}) {
  // Drag-and-drop removed (Sprint Apr-30). Stage changes fire only through
  // the DispositionPickerModal → advancer service. Attempting drag shows toast.
  const [dragToastVisible, setDragToastVisible] = useState(false);

  const handleDragAttempt = () => {
    setDragToastVisible(true);
    setTimeout(() => setDragToastVisible(false), 3000);
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* No-drag toast */}
      {dragToastVisible && (
        <div style={{
          position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--ink-0, #1B1713)', color: 'var(--ivory-1, #FAF7EF)',
          padding: '10px 18px', borderRadius: '10px', fontSize: '13px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)', zIndex: 9999, pointerEvents: 'none',
          maxWidth: '360px', textAlign: 'center',
        }}>
          Stages advance via call outcomes / proposal events. Click the lead to log a call disposition.
        </div>
      )}

      {pipelineData?.pipelineName && (
        <div className="mb-3 flex items-center gap-2 text-xs text-[var(--text-muted)]">
          <TrendingUp size={12} />
          <span>Pipeline: <strong className="text-[var(--text-secondary)]">{pipelineData.pipelineName}</strong></span>
          <span>· {pipelineData.stages?.reduce((sum: number, s: any) => sum + (s.deals?.length ?? 0), 0) ?? 0} deals</span>
        </div>
      )}

      <div
        className="flex gap-4 overflow-x-auto pb-4 min-h-[400px]"
        onMouseDown={handleDragAttempt}
      >
        {KANBAN_COLUMNS.map((col) => {
          // Filter by resolved canonical stage (handles legacy leads too)
          const colLeads = leads.filter((l) => resolveCanonical(l) === col.id);
          const hex = col.colorHex;
          return (
            <div
              key={col.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: '240px',
                maxWidth: '240px',
                borderRadius: '12px',
                border: `1.5px solid ${hex}33`,
                background: `${hex}0D`,
                padding: '12px',
              }}
            >
              {/* Column header */}
              <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{
                  margin: 0, fontSize: '11px', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  color: hex,
                }}>
                  {col.label}
                </h3>
                <span style={{
                  borderRadius: '9999px', background: 'var(--ivory-2, #F4F0E4)',
                  padding: '2px 8px', fontSize: '11px', color: 'var(--ink-3, #8B8373)',
                }}>
                  {colLeads.length}
                </span>
              </div>

              {/* Cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                {colLeads.length === 0 ? (
                  <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', padding: '24px 0' }}>
                    <p style={{ fontSize: '12px', color: 'var(--ink-3, #8B8373)' }}>No leads</p>
                  </div>
                ) : (
                  colLeads.slice(0, 10).map((lead) => (
                    <KanbanCard
                      key={lead.id}
                      lead={lead}
                      stageColorHex={hex}
                      maxMissedFollowUps={maxMissedFollowUps}
                      onClick={() => onCardClick(lead)}
                      onOpenDetail={() => onOpenDetail(lead)}
                    />
                  ))
                )}
                {colLeads.length > 10 && (
                  <p style={{ textAlign: 'center', fontSize: '11px', color: 'var(--ink-3, #8B8373)' }}>
                    +{colLeads.length - 10} more
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KanbanCard({
  lead,
  stageColorHex,
  maxMissedFollowUps,
  onClick,
  onOpenDetail,
}: {
  lead: Lead;
  stageColorHex: string;
  maxMissedFollowUps: number;
  onClick: () => void;
  onOpenDetail: () => void;
}) {
  // Pipeline-Stage-Overhaul (May 2 2026): heat-tag chip removed from kanban
  // cards — Stage column header is the single source of truth for lead state.
  const canonicalStage = resolveCanonical(lead);
  const isTerminal = canonicalStage === 'won' || canonicalStage === 'lost';
  const isFollowUpOverdue = lead.followUpDate && new Date(lead.followUpDate) < new Date() && !isTerminal;
  const missedCount = lead.consecutiveMissedFollowUps ?? 0;
  const followUpTotal = lead.followUpTotal ?? maxMissedFollowUps;

  return (
    <div
      onClick={onClick}
      title="Click to log a call disposition"
      style={{
        cursor: 'pointer',
        borderRadius: '10px',
        border: `1.5px solid var(--line, #E7E0CF)`,
        background: 'var(--ivory-0, #FEFCF7)',
        padding: '10px 12px',
        transition: 'border-color 150ms, box-shadow 150ms',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = stageColorHex + '80';
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--line, #E7E0CF)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
      }}
    >
      {/* Stage dot indicator */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '6px', gap: '6px' }}>
        <p style={{
          margin: 0, fontSize: '13px', fontWeight: 600,
          color: 'var(--ink-0, #1B1713)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
        }}>
          {lead.name || 'Unknown'}
        </p>
        {/* Open full detail on ⓘ button without opening disposition picker */}
        <button
          onClick={(e) => { e.stopPropagation(); onOpenDetail(); }}
          title="Open lead detail"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--ink-3, #8B8373)', padding: '0 2px', flexShrink: 0,
            fontSize: '11px', fontWeight: 700, lineHeight: 1,
          }}
        >
          ⓘ
        </button>
      </div>

      {lead.company && (
        <p style={{ margin: '0 0 6px', fontSize: '11px', color: 'var(--ink-3, #8B8373)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {lead.company}
        </p>
      )}
      {lead.product && (
        <p style={{ margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--ink-2, #5E564B)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <Package size={10} style={{ flexShrink: 0 }} />{lead.product}
        </p>
      )}

      {/* Badge row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
        {/* Repeat customer chip */}
        {lead.isRepeatCustomer && (
          <RepeatCustomerChip
            prevDealValue={lead.metadata?.prevDealValue}
            prevDealClosedAt={lead.metadata?.prevDealClosedAt}
          />
        )}

        {/* Lost reason badge (only on lost column) */}
        {canonicalStage === 'lost' && (
          <LostReasonBadge
            reason={lead.exactLostReason ?? null}
            competitorName={lead.exactLostCompetitorName}
            freeText={lead.exactLostFreeText}
          />
        )}

        {/* Follow-up streak badge (missedCount > 0, not in terminal stage) */}
        {!isTerminal && missedCount > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            borderRadius: '9999px', padding: '2px 7px',
            fontSize: '10px', fontWeight: 600,
            background: missedCount >= followUpTotal ? 'rgba(138,42,58,0.1)' : 'var(--saffron-50, #FBF3DF)',
            color: missedCount >= followUpTotal ? '#8A2A3A' : 'var(--saffron-600, #B87A2E)',
            border: `1px solid ${missedCount >= followUpTotal ? 'rgba(138,42,58,0.2)' : 'var(--saffron-100, #F5E7C9)'}`,
            whiteSpace: 'nowrap',
          }}>
            <Clock size={8} />
            Follow-up {missedCount} / {followUpTotal}
          </span>
        )}

        {/* Pipeline-Stage-Overhaul (May 2 2026): heat-tag chip removed —
            stage column already conveys lead state. */}

        {lead.score != null && (
          <span style={{
            display: 'inline-flex', borderRadius: '9999px', padding: '2px 6px',
            fontSize: '9px', fontWeight: 700,
          }} className={`${lead.score >= 70 ? 'bg-green-500/10 text-green-400' : lead.score >= 40 ? 'bg-yellow-500/10 text-yellow-400' : 'bg-red-500/10 text-red-400'}`}>
            {lead.score}
          </span>
        )}

        {isFollowUpOverdue && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            borderRadius: '9999px', padding: '2px 7px',
            fontSize: '9px', fontWeight: 500,
            background: 'rgba(180,88,0,0.1)', color: 'var(--saffron-600, #B87A2E)',
            whiteSpace: 'nowrap',
          }}>
            <Clock size={8} />Overdue
          </span>
        )}

        {(lead.callAttempts ?? 0) > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            fontSize: '9px', color: 'var(--ink-3, #8B8373)',
          }}>
            <PhoneCall size={8} />{lead.callAttempts}
          </span>
        )}
      </div>

      {lead.assignedToName && (
        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{
            width: '16px', height: '16px', borderRadius: '9999px',
            background: `${stageColorHex}20`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '9px', fontWeight: 700, color: stageColorHex, flexShrink: 0,
          }}>
            {lead.assignedToName.charAt(0)}
          </div>
          <span style={{ fontSize: '10px', color: 'var(--ink-3, #8B8373)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {lead.assignedToName}
          </span>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function KPICard({ label, value, color }: { label: string; value: number; color: string }) {
  const textMap: Record<string, string> = { indigo: 'text-indigo-400', blue: 'text-blue-400', cyan: 'text-cyan-400', sky: 'text-sky-400', purple: 'text-purple-400', orange: 'text-orange-400', green: 'text-green-400', red: 'text-red-400' };
  return (
    <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-3">
      <p className="text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">{label}</p>
      <p className={`mt-1 text-xl font-bold ${textMap[color] ?? 'text-[var(--text-primary)]'}`}>{value.toLocaleString('en-IN')}</p>
    </div>
  );
}

function TabButton({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count?: number; color?: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${active ? 'bg-indigo-600 text-white' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] hover:text-[var(--text-primary)]'}`}
    >
      {label}
      {count != null && (
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${active ? 'bg-white/20' : 'bg-[var(--bg-inset)] text-[var(--text-muted)]'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

function InputField({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      />
    </div>
  );
}
